import { safeMedicalCertificateFileName as safeStorageName } from "../../../utils/medicalCertificateFileName";
import AnnualMaskDoctorEmailStatus from "./AnnualMaskDoctorEmailStatus";
import { getAnnualMaskDoctorSkipUnavailableReason } from "./annualMaskPurchaseUtils";
import AnnualMaskWorkflowProgress, { AnnualMaskStepStatus } from "./AnnualMaskWorkflowProgress";
import { isAnnualMaskPhysician } from "./annualMaskPhysicianQuery";
import { canUploadAnnualMaskCertificate, isAnnualMaskCertificateUploadLocked, saveAnnualMaskSignedCertificate } from "./annualMaskPhysicianUpload";
import AnnualMaskCompleteButton from "./AnnualMaskCompleteButton";
import AnnualMaskTestEmailButton from "./AnnualMaskTestEmailButton";
import { queueAnnualMaskEmail } from "./queueAnnualMaskEmail";
import AnnualMaskReceiptDate, { AnnualMaskReceiptDateText } from "./AnnualMaskReceiptDate";
import { isAnnualMaskReceiptDateValid, withAnnualMaskDefaultSale } from "./annualMaskPurchaseUtils";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import swal from "sweetalert";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  getBytes,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { db, storage } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import {
  notifyError,
  notifyTopRight,
  notifyWarning,
} from "../toast/SetToast";
import {
  ANNUAL_MASK_BATCHES_COLLECTION,
  ANNUAL_MASK_CASES_COLLECTION,
  ANNUAL_MASK_SOCIAL_SECURITY_DECISION,
  ANNUAL_MASK_STATUS,
  advanceAnnualMaskStatus,
  canSkipAnnualMaskDoctorStep,
  canSkipAnnualMaskSignedCertificate,
  isAnnualMaskSignedCertificateStepComplete,
  canManageAnnualMaskPurchase,
  findAnnualMaskDoctorForFollowup,
  getAnnualMaskCaseStatusForSaleOrder,
  getAnnualMaskCasePath,
  getAnnualMaskDefaultBatchName,
  getAnnualMaskNextBatchName,
  getAnnualMaskSelectedSaleId,
  getAnnualMaskSelectedSaleName,
  getAnnualMaskSocialSecurityDecision,
  getAnnualMaskStatusConfig,
  isAnnualMaskDraftEditable,
} from "./annualMaskPurchaseUtils";
import { normalizeSleepStudyReportDocs } from "./cpapFollowupFileUtils";
import {
  extractSleepStudyClinicalValues,
  formatAnnualMaskPressureRange,
  getAnnualMaskFileKey,
  getAnnualMaskLocalUsageFile,
  getAnnualMaskSleepStudyReportReference,
  isAnnualMaskPdfFile,
  sortAnnualMaskFilesByNewest,
} from "./annualMaskClinicalDataUtils";
import { extractAnnualMaskPdfText, readAnnualMaskSignedPressure } from "./annualMaskClinicalPdfReader";
import AnnualMaskPressureNotice from "./AnnualMaskPressureNotice";
import {
  canGenerateAnnualMaskMedicalCert,
  generateAnnualMaskMedicalCertBlob,
  getAnnualMaskMedicalCertFileName,
} from "../Export/ExportTab/annualMaskMedicalCert";
import { downloadAnnualMaskDoctorPackage } from "./annualMaskDoctorPackage";
import {
  createAnnualMaskSaleOrderWithStock,
  getAnnualMaskOrderPatientLabel,
} from "./annualMaskSaleOrder";

const safeText = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const AnnualMaskReviewField = ({ children, highlight = false, label }) => (
  <div
    className="border rounded px-2 py-2"
    style={{
      backgroundColor: highlight ? "#f0f7ff" : "#f8f9fa",
      borderColor: highlight ? "#b8d7f5" : "#e6e9ed",
      minHeight: 72,
      overflowWrap: "anywhere",
    }}
  >
    <div
      className="font-weight-bold text-muted mb-1"
      style={{
        fontSize: 10,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 12, lineHeight: 1.45 }}>{children}</div>
  </div>
);


const isPdf = (file) =>
  Boolean(
    file &&
      (file.type === "application/pdf" || /\.pdf$/i.test(file.name || "")),
  );

const getCaseFileUrl = async (fileData = {}) => {
  if (fileData.storagePath) {
    return getDownloadURL(ref(storage, fileData.storagePath));
  }
  if (/^https?:\/\//i.test(safeText(fileData.url))) return fileData.url;
  return "";
};

const getCaseFileMailAttachment = async (
  fileData = {},
  label = "File",
  fallbackFileName = "attachment.pdf",
  fallbackContentType = "application/pdf",
) => {
  const path = await getCaseFileUrl(fileData);
  if (!path) throw new Error(`${label} is unavailable.`);

  return {
    filename: fileData.fileName || fileData.savedFileName || fallbackFileName,
    path,
    contentType: fileData.contentType || fallbackContentType,
  };
};

const isEmail = (value) => /\S+@\S+\.\S+/.test(safeText(value));

const isMaskAccessory = (item = {}) => {
  const category = safeText(
    item.category || item.productType,
  ).toLocaleLowerCase();
  const itemName = safeText(item.itemName).toLocaleLowerCase();
  return (
    category === "mask" ||
    itemName.includes("mask") ||
    itemName.includes("หน้ากาก")
  );
};

const formatLots = (lots = []) =>
  lots
    .filter((lot) => Number(lot.stockQuantity) > 0)
    .map((lot) => `${lot.lotNo || lot.id}: ${lot.stockQuantity}`)
    .join(", ") || "No available lot";

const getActorName = (currentUser) =>
  safeText(currentUser && (currentUser.displayName || currentUser.email));

const isAnnualMaskPurchaseResultLocked = (caseItem = {}) =>
  [
    ANNUAL_MASK_STATUS.READY_TO_PURCHASE,
    ANNUAL_MASK_STATUS.ORDER_CREATED,
    ANNUAL_MASK_STATUS.ORDER_COMPLETED,
  ].includes(caseItem.status) || Boolean(caseItem.saleOrderId);

const getFollowupPatient = (followup = {}) => ({
  hn: safeText(followup.hn),
  firstname: safeText(followup.firstname),
  lastname: safeText(followup.lastname),
  firstnameTh: safeText(followup.firstnameTh || followup.firstname),
  lastnameTh: safeText(followup.lastnameTh || followup.lastname),
  mobile: safeText(followup.mobile || followup.phone),
  refHn: safeText(followup.refHn || followup.hospitalRef),
  refCustomer: safeText(followup.refCustomer || followup.customer),
  nodeRef: safeText(followup.nodeRef || followup.node),
});

const getPatientName = (patient = {}) =>
  [
    patient.firstnameTh || patient.firstname,
    patient.lastnameTh || patient.lastname,
  ]
    .map(safeText)
    .filter(Boolean)
    .join(" ");

const toTimestampText = (value) => {
  if (!value) return "-";
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const sortCasesNewest = (cases = []) =>
  [...cases].sort(
    (left, right) =>
      getTimestampMillis(right.createdAt || right.updatedAt) -
      getTimestampMillis(left.createdAt || left.updatedAt),
  );

const makeCreateForm = (followup = {}) => ({
  batchId: "",
  batchName: getAnnualMaskDefaultBatchName(followup),
  doctorId: "",
  doctorName: "",
  doctorEmail: "",
  ahi: "",
  minSpo2: "",
  minPressure: safeText(followup.minPressure),
  maxPressure: safeText(followup.maxPressure),
  latestSleepStudyDate: "",
  usageFile: null,
});

const makeLinkedCaseForm = (caseItem = {}, followup = {}) => {
  const certificateData = caseItem.certificateData || {};

  return {
    ...makeCreateForm(followup),
    batchId: safeText(caseItem.batchId),
    batchName:
      safeText(caseItem.batchName) || getAnnualMaskDefaultBatchName(followup),
    doctorId: safeText(caseItem.doctorId),
    doctorName: safeText(caseItem.doctorName),
    doctorEmail: safeText(caseItem.doctorEmail),
    ahi: safeText(certificateData.ahi),
    minSpo2: safeText(certificateData.minSpo2),
    minPressure: safeText(
      certificateData.minPressure || followup.minPressure,
    ),
    maxPressure: safeText(
      certificateData.maxPressure || followup.maxPressure,
    ),
    latestSleepStudyDate: safeText(certificateData.latestSleepStudyDate),
    usageFile: null,
  };
};

const getDoctorFormValues = (doctor) => ({
  doctorId: doctor ? safeText(doctor.id) : "",
  doctorName: doctor
    ? safeText(doctor.name || doctor.doctorName || doctor.doctorId || doctor.id)
    : "",
  doctorEmail: doctor ? safeText(doctor.email) : "",
});

const getReportName = (file = {}) =>
  safeText(file.fileName || file.savedFileName) || "Unnamed PDF";

const formatClinicalValue = (value, unit = "") => {
  const text = safeText(value);
  return text ? `${text}${unit ? ` ${unit}` : ""}` : "-";
};

const getSourceUsageReport = (file = {}) => ({
  fileName: getReportName(file),
  savedFileName: safeText(file.savedFileName),
  storagePath: safeText(file.storagePath),
  url: safeText(file.url),
  contentType: safeText(file.contentType) || "application/pdf",
  uploadedAt: file.uploadedAt || "",
  uploadedBy: safeText(file.uploadedBy),
  sourceFollowupUuid: safeText(file.followupUuid),
  source: "cpap-followup",
  annualMaskOwned: false,
});

const AnnualMaskPurchaseEventSection = ({
  followup = {},
  pendingUsageReports = [],
  readOnly = false,
  onCaseLinked,
}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [batches, setBatches] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [sales, setSales] = useState([]);
  const [masks, setMasks] = useState([]);
  const [masksLoading, setMasksLoading] = useState(false);
  const [eventCases, setEventCases] = useState([]);
  const [storedLinkedCase, setLinkedCase] = useState(null);
  const linkedCase = useMemo(
    () => withAnnualMaskDefaultSale(storedLinkedCase, sales),
    [storedLinkedCase, sales],
  );
  const [patientSleepStudyReports, setPatientSleepStudyReports] = useState([]);
  const [selectedUsageReportKey, setSelectedUsageReportKey] = useState("");
  const [selectedSleepStudyReportKey, setSelectedSleepStudyReportKey] =
    useState("");
  const [fetchingClinicalData, setFetchingClinicalData] = useState(false);
  const [editingLinkedCase, setEditingLinkedCase] = useState(false);
  const [creatingAnotherCase, setCreatingAnotherCase] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [createForm, setCreateForm] = useState(() =>
    makeCreateForm(followup),
  );
  const signedCertificateInputRef = useRef(null);

  const followupId = safeText(followup.uuid || followup.id);
  const hn = safeText(followup.hn);
  const followupDoctorConsult = safeText(followup.doctorConsult);
  const annualLink = followup.annualMaskPurchase || null;
  const canManage = canManageAnnualMaskPurchase(currentUser) && !readOnly;
  const physicianOnly = isAnnualMaskPhysician(currentUser);
  const canUploadCertificate = !readOnly && canUploadAnnualMaskCertificate(linkedCase, currentUser);
  const canCreate = Boolean(followupId && hn && canManage);
  const therapyMinPressure = safeText(followup.minPressure);
  const therapyMaxPressure = safeText(followup.maxPressure);

  const sortedBatches = useMemo(
    () =>
      [...batches].sort((left, right) =>
        safeText(left.batchName || left.id).localeCompare(
          safeText(right.batchName || right.id),
        ),
      ),
    [batches],
  );

  const usageReports = useMemo(() => {
    const reportMap = new Map();
    const reportFiles = [
      ...(Array.isArray(followup.usageReports) ? followup.usageReports : []),
      ...(Array.isArray(pendingUsageReports) ? pendingUsageReports : []),
    ];

    reportFiles.filter(isAnnualMaskPdfFile).forEach((file, index) => {
      const key = getAnnualMaskFileKey(file, index);
      if (!reportMap.has(key)) reportMap.set(key, file);
    });

    return sortAnnualMaskFilesByNewest(Array.from(reportMap.values()));
  }, [followup.usageReports, pendingUsageReports]);

  const sleepStudyReports = useMemo(() => {
    const reportMap = new Map();
    const reportFiles = [
      ...(Array.isArray(followup.sleepStudyReports)
        ? followup.sleepStudyReports
        : []),
      ...patientSleepStudyReports,
    ];

    reportFiles.filter(isAnnualMaskPdfFile).forEach((file, index) => {
      const key = getAnnualMaskFileKey(file, index);
      if (!reportMap.has(key)) reportMap.set(key, file);
    });

    return sortAnnualMaskFilesByNewest(Array.from(reportMap.values()));
  }, [followup.sleepStudyReports, patientSleepStudyReports]);

  const selectedUsageReport = useMemo(
    () =>
      usageReports.find(
        (file, index) =>
          getAnnualMaskFileKey(file, index) === selectedUsageReportKey,
      ) || null,
    [usageReports, selectedUsageReportKey],
  );

  const selectedSleepStudyReport = useMemo(
    () =>
      sleepStudyReports.find(
        (file, index) =>
          getAnnualMaskFileKey(file, index) === selectedSleepStudyReportKey,
      ) || null,
    [sleepStudyReports, selectedSleepStudyReportKey],
  );

  const suggestedBatchName = getAnnualMaskNextBatchName(
    followup, eventCases, createForm.usageFile ? null : selectedUsageReport,
  );
  useEffect(() => {
    if (loading || editingLinkedCase || createForm.batchId || (linkedCase && !creatingAnotherCase)) return;
    setCreateForm((current) => current.batchName === suggestedBatchName
      ? current : { ...current, batchName: suggestedBatchName });
  }, [suggestedBatchName, loading, editingLinkedCase, createForm.batchId, linkedCase, creatingAnotherCase]);

  useEffect(() => {
    setCreateForm(makeCreateForm(followup));
    setEventCases([]);
    setLinkedCase(null);
    setEditingLinkedCase(false);
    setCreatingAnotherCase(false);
    // The event id is stable while an operator works in this tab. Reset only
    // when a different CPAP follow-up event is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followupId]);

  useEffect(() => {
    let active = true;

    const loadPatientSleepStudyReports = async () => {
      if (!hn) {
        if (active) setPatientSleepStudyReports([]);
        return;
      }

      try {
        const snapshot = await getDocs(collection(db, "patients", hn, "report"));
        if (active) {
          setPatientSleepStudyReports(
            normalizeSleepStudyReportDocs(snapshot.docs, hn),
          );
        }
      } catch (error) {
        console.warn("Unable to load sleep study reports for Annual Mask Purchase", error);
        if (active) setPatientSleepStudyReports([]);
      }
    };

    loadPatientSleepStudyReports();
    return () => {
      active = false;
    };
  }, [hn]);

  useEffect(() => {
    setSelectedUsageReportKey((currentKey) => {
      const hasCurrentSelection = usageReports.some(
        (file, index) => getAnnualMaskFileKey(file, index) === currentKey,
      );
      if (hasCurrentSelection) return currentKey;
      return usageReports.length ? getAnnualMaskFileKey(usageReports[0], 0) : "";
    });
  }, [usageReports]);

  useEffect(() => {
    setSelectedSleepStudyReportKey((currentKey) => {
      const hasCurrentSelection = sleepStudyReports.some(
        (file, index) => getAnnualMaskFileKey(file, index) === currentKey,
      );
      if (hasCurrentSelection) return currentKey;
      return sleepStudyReports.length
        ? getAnnualMaskFileKey(sleepStudyReports[0], 0)
        : "";
    });
  }, [sleepStudyReports]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!followupId) {
        if (active) {
          setLinkedCase(null);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        const [batchSnapshot, doctorSnapshot, salesSnapshot] = await Promise.all([
          getDocs(collection(db, ANNUAL_MASK_BATCHES_COLLECTION)),
          getDocs(collection(db, "doctors")),
          getDocs(
            query(
              collection(db, "sales"),
              where("status", "==", "Available"),
            ),
          ),
        ]);
        if (!active) return;

        const caseSnapshots = await Promise.all(
          batchSnapshot.docs.map((batchDoc) =>
            getDocs(
              collection(
                db,
                ANNUAL_MASK_BATCHES_COLLECTION,
                batchDoc.id,
                ANNUAL_MASK_CASES_COLLECTION,
              ),
            ),
          ),
        );
        if (!active) return;

        const nextDoctors = doctorSnapshot.docs
          .map((doctorDoc) => ({ id: doctorDoc.id, ...doctorDoc.data() }))
          .filter((doctor) =>
            safeText(doctor.name || doctor.doctorName || doctor.id),
          )
          .sort((left, right) =>
            safeText(left.name || left.doctorName || left.id).localeCompare(
              safeText(right.name || right.doctorName || right.id),
            ),
          );
        const nextBatches = batchSnapshot.docs.map((batchDoc) => ({
          id: batchDoc.id,
          ...batchDoc.data(),
        }));
        const nextSales = salesSnapshot.docs
          .map((saleDoc) => ({ id: saleDoc.id, ...saleDoc.data() }))
          .filter((sale) => safeText(sale.name))
          .sort((left, right) =>
            safeText(left.name).localeCompare(safeText(right.name)),
          );
        setBatches(nextBatches);
        setDoctors(nextDoctors);
        setSales(nextSales);

        const batchNames = nextBatches.reduce((names, batch) => {
          names[batch.id] = safeText(batch.batchName);
          return names;
        }, {});
        let nextEventCases = caseSnapshots
          .reduce((caseDocs, snapshot) => caseDocs.concat(snapshot.docs), [])
          .map((caseDoc) => {
            const caseData = caseDoc.data();
            const batchId = safeText(caseData.batchId);
            return {
              ...caseData,
              id: caseDoc.id,
              refPath: caseDoc.ref.path,
              batchId,
              batchName: batchNames[batchId] || safeText(caseData.batchName),
            };
          })
          .filter(
            (caseItem) =>
              safeText(caseItem.sourceFollowupId) === followupId,
          );

        const linkedCasePath = safeText(annualLink && annualLink.casePath);
        if (
          linkedCasePath &&
          !nextEventCases.some((caseItem) => caseItem.refPath === linkedCasePath)
        ) {
          const legacyCaseSnapshot = await getDoc(doc(db, linkedCasePath));
          if (!active) return;
          if (legacyCaseSnapshot.exists()) {
            const caseData = legacyCaseSnapshot.data();
            const batchId = safeText(caseData.batchId || annualLink.batchId);
            nextEventCases.push({
              ...caseData,
              id: legacyCaseSnapshot.id,
              refPath: linkedCasePath,
              batchId,
              batchName: batchNames[batchId] || safeText(caseData.batchName),
            });
          }
        }

        nextEventCases = sortCasesNewest(nextEventCases);
        const nextLinkedCase =
          nextEventCases.find(
            (caseItem) => caseItem.refPath === linkedCasePath,
          ) || nextEventCases[0] || null;
        setEventCases(nextEventCases);
        setLinkedCase(nextLinkedCase);
        setCreatingAnotherCase(false);
        if (!nextLinkedCase) {
          setCreateForm({
            ...makeCreateForm(followup),
            batchName: getAnnualMaskNextBatchName(followup, nextEventCases),
          });
        }

      } catch (error) {
        console.error("Unable to load Annual Mask Purchase event section", error);
        if (active) notifyError("Cannot load Annual Mask Purchase information.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
    // Only re-load when this event or its saved workflow link changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followupId, annualLink && annualLink.casePath]);

  useEffect(() => {
    if ((linkedCase && !creatingAnotherCase) || !doctors.length) return;

    const eventDoctor = findAnnualMaskDoctorForFollowup(
      doctors,
      followupDoctorConsult,
    );
    setCreateForm((current) => {
      return {
        ...current,
        ...getDoctorFormValues(eventDoctor),
      };
    });
  }, [doctors, followupDoctorConsult, linkedCase, creatingAnotherCase]);

  useEffect(() => {
    let active = true;

    const loadMaskInventory = async () => {
      if (!linkedCase) {
        if (active) setMasks([]);
        return;
      }

      setMasksLoading(true);
      try {
        const accessorySnapshot = await getDocs(collection(db, "accessories"));
        const maskRows = accessorySnapshot.docs
          .map((accessoryDoc) => ({
            id: accessoryDoc.id,
            ...accessoryDoc.data(),
          }))
          .filter(isMaskAccessory);
        const nextMasks = await Promise.all(
          maskRows.map(async (mask) => {
            try {
              const lotSnapshot = await getDocs(
                collection(db, "accessories", mask.id, "lots"),
              );
              return {
                ...mask,
                lots: lotSnapshot.docs.map((lotDoc) => ({
                  id: lotDoc.id,
                  ...lotDoc.data(),
                })),
              };
            } catch (error) {
              console.warn("Unable to load Annual Mask inventory lots", error);
              return { ...mask, lots: [] };
            }
          }),
        );
        if (active) {
          setMasks(
            nextMasks.sort((left, right) =>
              safeText(left.itemName).localeCompare(safeText(right.itemName)),
            ),
          );
        }
      } catch (error) {
        console.warn("Unable to load Annual Mask inventory", error);
        if (active) setMasks([]);
      } finally {
        if (active) setMasksLoading(false);
      }
    };

    loadMaskInventory();
    return () => {
      active = false;
    };
  }, [linkedCase && linkedCase.id]);

  const updateCreateField = (field, value) => {
    setCreateForm((current) => ({ ...current, [field]: value }));
  };

  const selectDoctor = (doctorId) => {
    const doctor = doctors.find((item) => item.id === doctorId);
    setCreateForm((current) => ({
      ...current,
      ...getDoctorFormValues(doctor),
    }));
  };

  const isCurrentFollowupLink = (caseItem) => {
    if (!caseItem || !annualLink) return false;
    const linkedPath = safeText(annualLink.casePath);
    if (linkedPath) return linkedPath === safeText(caseItem.refPath);
    return (
      safeText(annualLink.batchId) === safeText(caseItem.batchId) &&
      safeText(annualLink.caseId) === safeText(caseItem.id)
    );
  };

  const replaceEventCase = (nextCase) => {
    setEventCases((current) =>
      sortCasesNewest(
        current.map((caseItem) =>
          caseItem.refPath === nextCase.refPath ? nextCase : caseItem,
        ),
      ),
    );
  };

  const selectEventCase = (casePath) => {
    const nextCase = eventCases.find(
      (caseItem) => caseItem.refPath === casePath,
    );
    if (!nextCase) return;
    setLinkedCase(nextCase);
    setCreateForm(makeLinkedCaseForm(nextCase, followup));
    setEditingLinkedCase(false);
    setCreatingAnotherCase(false);
  };

  const beginAnotherCase = () => {
    if (!canManage) return;
    const eventDoctor = findAnnualMaskDoctorForFollowup(
      doctors,
      followupDoctorConsult,
    );
    setCreateForm({
      ...makeCreateForm(followup),
      batchName: getAnnualMaskNextBatchName(followup, eventCases, selectedUsageReport),
      ...getDoctorFormValues(eventDoctor),
    });
    setEditingLinkedCase(false);
    setCreatingAnotherCase(true);
  };

  const cancelAnotherCase = () => {
    setCreatingAnotherCase(false);
    if (linkedCase) setCreateForm(makeLinkedCaseForm(linkedCase, followup));
  };

  const beginLinkedCaseEdit = () => {
    if (!canManage || !isAnnualMaskDraftEditable(linkedCase)) return;
    const caseDoctor = findAnnualMaskDoctorForFollowup(
      doctors,
      linkedCase.doctorId || linkedCase.doctorName || linkedCase.doctorEmail,
    );
    setCreateForm({
      ...makeLinkedCaseForm(linkedCase, followup),
      ...(caseDoctor ? getDoctorFormValues(caseDoctor) : {}),
    });
    setEditingLinkedCase(true);
  };

  const cancelLinkedCaseEdit = () => {
    setCreateForm(makeLinkedCaseForm(linkedCase, followup));
    setEditingLinkedCase(false);
  };

  const saveLinkedCaseChanges = async () => {
    if (!canManage || !linkedCase || !isAnnualMaskDraftEditable(linkedCase)) {
      notifyWarning(
        "Batch details can only be edited before the doctor email is sent.",
      );
      return;
    }

    const batchName = safeText(createForm.batchName);
    if (!batchName) {
      notifyWarning("Enter a purchase batch name.");
      return;
    }
    if (!createForm.doctorName || !createForm.doctorEmail) {
      notifyWarning("Select a doctor with an email address.");
      return;
    }
    if (createForm.usageFile && !isPdf(createForm.usageFile)) {
      notifyWarning("Upload the CPAP Usage Report as a PDF file.");
      return;
    }

    setSaving(true);
    let replacementUsageRef = null;
    let replacementSaved = false;
    try {
      const actor = getActorName(currentUser);
      let usageReport = linkedCase.usageReport || null;

      if (createForm.usageFile) {
        const savedFileName = `${Date.now()}_${safeStorageName(
          createForm.usageFile.name,
        )}`;
        const storagePath = `annual-mask-purchases/${hn}/${linkedCase.batchId}/${linkedCase.id}/usage-report/${savedFileName}`;
        replacementUsageRef = ref(storage, storagePath);
        await uploadBytes(replacementUsageRef, createForm.usageFile, {
          contentType: createForm.usageFile.type || "application/pdf",
        });
        const url = await getDownloadURL(replacementUsageRef);
        usageReport = {
          fileName: createForm.usageFile.name,
          savedFileName,
          storagePath,
          url,
          contentType: createForm.usageFile.type || "application/pdf",
          uploadedAt: new Date().toISOString(),
          uploadedBy: actor,
          source: "annual-mask-upload",
          annualMaskOwned: true,
        };
      }

      const certificateData = {
        ahi: safeText(createForm.ahi),
        minSpo2: safeText(createForm.minSpo2),
        minPressure: safeText(createForm.minPressure),
        maxPressure: safeText(createForm.maxPressure),
        pressure: formatAnnualMaskPressureRange({
          minPressure: createForm.minPressure,
          maxPressure: createForm.maxPressure,
        }),
        latestSleepStudyDate: safeText(createForm.latestSleepStudyDate),
      };
      const caseValues = {
        batchName,
        doctorId: safeText(createForm.doctorId),
        doctorName: safeText(createForm.doctorName),
        doctorEmail: safeText(createForm.doctorEmail),
        certificateData,
        usageReport,
        updatedAt: serverTimestamp(),
        updatedBy: actor,
      };
      const updates = writeBatch(db);
      updates.set(
        doc(db, ANNUAL_MASK_BATCHES_COLLECTION, linkedCase.batchId),
        {
          batchName,
          updatedAt: serverTimestamp(),
          updatedBy: actor,
        },
        { merge: true },
      );
      updates.update(doc(db, linkedCase.refPath), caseValues);
      if (isCurrentFollowupLink(linkedCase)) {
        updates.update(doc(db, "cpap-followup", followupId), {
          "extendedProps.annualMaskPurchase.batchName": batchName,
          "extendedProps.annualMaskPurchase.updatedAt": serverTimestamp(),
          updatedAt: serverTimestamp(),
          updatedBy: actor,
        });
      }
      await updates.commit();
      replacementSaved = true;

      if (
        replacementUsageRef &&
        linkedCase.usageReport &&
        linkedCase.usageReport.storagePath &&
        linkedCase.usageReport.annualMaskOwned !== false &&
        linkedCase.usageReport.storagePath !== replacementUsageRef.fullPath
      ) {
        try {
          await deleteObject(ref(storage, linkedCase.usageReport.storagePath));
        } catch (cleanupError) {
          console.warn("Unable to remove replaced Annual Mask Usage Report", cleanupError);
        }
      }

      const nextCase = {
        ...linkedCase,
        ...caseValues,
        batchName,
        certificateData,
        usageReport,
        updatedAt: new Date().toISOString(),
      };
      setLinkedCase(nextCase);
      setEventCases((current) =>
        sortCasesNewest(
          current.map((caseItem) =>
            caseItem.batchId === linkedCase.batchId
              ? {
                  ...caseItem,
                  ...(caseItem.refPath === nextCase.refPath ? nextCase : {}),
                  batchName,
                }
              : caseItem,
          ),
        ),
      );
      setCreateForm(makeLinkedCaseForm(nextCase, followup));
      setBatches((current) =>
        current.map((batch) =>
          batch.id === linkedCase.batchId ? { ...batch, batchName } : batch,
        ),
      );
      setEditingLinkedCase(false);
      if (onCaseLinked && isCurrentFollowupLink(linkedCase)) {
        onCaseLinked({
          ...(annualLink || {}),
          batchId: linkedCase.batchId,
          batchName,
          caseId: linkedCase.id,
          casePath: linkedCase.refPath,
          status: linkedCase.status || ANNUAL_MASK_STATUS.DRAFT,
        });
      }
      notifyTopRight("Annual Mask Purchase batch updated.");
    } catch (error) {
      console.error("Unable to update Annual Mask Purchase batch", error);
      if (replacementUsageRef && !replacementSaved) {
        try {
          await deleteObject(replacementUsageRef);
        } catch (cleanupError) {
          console.warn("Unable to clean up replacement Usage Report", cleanupError);
        }
      }
      notifyError(error.message || "Cannot update Annual Mask Purchase batch.");
    } finally {
      setSaving(false);
    }
  };

  const readStoredPdf = async (fileData = {}, label = "PDF") => {
    if (fileData.localFile) {
      return fileData.localFile;
    }

    if (fileData.storagePath) {
      const bytes = await getBytes(
        ref(storage, fileData.storagePath),
        50 * 1024 * 1024,
      );
      return new Blob([bytes], {
        type: fileData.contentType || "application/pdf",
      });
    }

    if (fileData.url) {
      const response = await fetch(fileData.url);
      if (!response.ok) throw new Error(`Unable to read ${label}.`);
      return response.blob();
    }

    throw new Error(`${label} is unavailable.`);
  };

  const openFilePreview = async (fileData, fallbackTitle = "File") => {
    if (!fileData) return;
    try {
      const url = await getCaseFileUrl(fileData);
      if (!url) {
        notifyWarning("This file is unavailable.");
        return;
      }
      setFilePreview({
        title: fileData.fileName || fallbackTitle,
        url,
      });
    } catch (error) {
      console.error("Unable to open Annual Mask file preview", error);
      notifyError("Cannot open the signed medical certificate.");
    }
  };

  const renderFilePreviewModal = () => (
    <Modal
      show={Boolean(filePreview)}
      onHide={() => setFilePreview(null)}
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>{filePreview && filePreview.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0" style={{ height: "80vh" }}>
        {filePreview && (
          <iframe
            title="Signed Annual Mask medical certificate preview"
            src={filePreview.url}
            width="100%"
            height="100%"
            style={{ border: 0 }}
          />
        )}
      </Modal.Body>
    </Modal>
  );

  const updateLinkedCaseWorkflow = async (values) => {
    if (!linkedCase || !linkedCase.refPath) {
      throw new Error("Annual Mask Purchase case is unavailable.");
    }

    const defaultedCase = withAnnualMaskDefaultSale({ ...linkedCase, ...values }, sales);
    if (defaultedCase.selectedSale) values = { ...values, selectedSale: defaultedCase.selectedSale };
    const actor = getActorName(currentUser);
    const nextStatus =
      values.status || linkedCase.status || ANNUAL_MASK_STATUS.DRAFT;
    const sourceFollowupPath =
      safeText(linkedCase.sourceFollowupPath) || `cpap-followup/${followupId}`;
    const updates = writeBatch(db);
    updates.update(doc(db, linkedCase.refPath), {
      ...values,
      updatedAt: serverTimestamp(),
      updatedBy: actor,
    });
    updates.set(
      doc(db, ANNUAL_MASK_BATCHES_COLLECTION, linkedCase.batchId),
      {
        updatedAt: serverTimestamp(),
        updatedBy: actor,
      },
      { merge: true },
    );
    if (isCurrentFollowupLink(linkedCase)) {
      updates.update(doc(db, sourceFollowupPath), {
        "extendedProps.annualMaskPurchase.status": nextStatus,
        "extendedProps.annualMaskPurchase.updatedAt": serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: actor,
      });
    }
    await updates.commit();

    let nextCase = {
      ...linkedCase,
      ...values,
      status: nextStatus,
      updatedAt: new Date().toISOString(),
      updatedBy: actor,
    };
    try {
      const caseSnapshot = await getDoc(doc(db, linkedCase.refPath));
      if (caseSnapshot.exists()) {
        nextCase = {
          ...caseSnapshot.data(),
          id: caseSnapshot.id,
          refPath: linkedCase.refPath,
          batchId: linkedCase.batchId,
          batchName: linkedCase.batchName,
        };
      }
    } catch (refreshError) {
      // The write already succeeded. Keep the local state usable and allow a
      // later event reload to resolve server timestamps.
      console.warn("Unable to refresh updated Annual Mask case", refreshError);
    }
    setLinkedCase(nextCase);
    replaceEventCase(nextCase);
    if (onCaseLinked && isCurrentFollowupLink(linkedCase)) {
      onCaseLinked({
        ...(annualLink || {}),
        batchId: nextCase.batchId,
        batchName: nextCase.batchName,
        caseId: nextCase.id,
        casePath: nextCase.refPath,
        status: nextStatus,
        updatedAt: new Date().toISOString(),
      });
    }
    return nextCase;
  };

  const getLinkedCaseSleepStudyReport = () => {
    const sources = [
      linkedCase && linkedCase.sleepStudyReport,
      selectedSleepStudyReport,
    ].filter(Boolean);

    for (const source of sources) {
      const report = getAnnualMaskSleepStudyReportReference(source);
      if (report.storagePath || report.url) return report;
    }

    return null;
  };

  const generateLinkedCaseMedicalCertificate = () => {
    const certificateData = linkedCase.certificateData || {};
    return generateAnnualMaskMedicalCertBlob({
      patient: linkedCase.patient || linkedCase,
      nodeRef: linkedCase.nodeRef || linkedCase.node,
      doctor: linkedCase.doctorName,
      ahi: certificateData.ahi,
      minSpo2: certificateData.minSpo2,
      minPressure: certificateData.minPressure,
      maxPressure: certificateData.maxPressure,
      pressure: certificateData.pressure,
      latestSleepStudyDate: certificateData.latestSleepStudyDate,
    });
  };

  const sendDoctorEmail = async () => {
    if (!canManage || !linkedCase) {
      notifyWarning("You do not have permission to send this doctor email.");
      return;
    }
    if (!isEmail(linkedCase.doctorEmail)) {
      notifyWarning("Select a doctor with a valid email address first.");
      return;
    }
    if (!linkedCase.usageReport) {
      notifyWarning("Upload a CPAP Usage Report before sending email.");
      return;
    }
    const sleepStudyReport = getLinkedCaseSleepStudyReport();
    if (!sleepStudyReport) {
      notifyWarning("Select a Sleep Study Report before sending email.");
      return;
    }

    setSaving(true);
    let generatedCertificateRef = null;
    let doctorMailQueued = false;
    let certificateSaveStarted = false;
    try {
      const certificateBlob = await generateLinkedCaseMedicalCertificate();
      const certificateFileName = getAnnualMaskMedicalCertFileName(
        linkedCase.patient || linkedCase,
      );
      const certificatePath = `annual-mask-purchases/${linkedCase.hn}/${linkedCase.batchId}/${linkedCase.id}/generated-medical-certificate/${safeStorageName(
        certificateFileName,
      )}`;
      generatedCertificateRef = ref(storage, certificatePath);
      await uploadBytes(generatedCertificateRef, certificateBlob, {
        contentType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const certificateUrl = await getDownloadURL(generatedCertificateRef);
      const [usageAttachment, sleepStudyAttachment] = await Promise.all([
        getCaseFileMailAttachment(
          linkedCase.usageReport,
          "CPAP Usage Report",
          "CPAP-Usage-Report.pdf",
        ),
        getCaseFileMailAttachment(
          sleepStudyReport,
          "Sleep Study Report",
          "Sleep-Study-Report.pdf",
        ),
      ]);
      const casePatient = linkedCase.patient || linkedCase;
      const casePatientName = getPatientName(casePatient) || linkedCase.hn;

      certificateSaveStarted = true;
      await updateLinkedCaseWorkflow({
        generatedMedicalCertificate: {
          fileName: certificateFileName,
          storagePath: certificatePath,
          url: certificateUrl,
          contentType:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          generatedAt: new Date().toISOString(),
          generatedBy: getActorName(currentUser),
        },
        sleepStudyReport,
      });

      await queueAnnualMaskEmail({
        caseItem: linkedCase,
        message: {
          subject: `Annual 1 Year Mask Purchase - ${casePatientName} (${linkedCase.hn})`,
          html: `
            <h3>Annual 1 Year Mask Purchase</h3>
            <p><strong>Patient:</strong> ${casePatientName}</p>
            <p><strong>HN:</strong> ${linkedCase.hn}</p>
            <p><strong>Doctor:</strong> ${linkedCase.doctorName || "-"}</p>
            <p>Please review and sign the attached Annual 1 Year Mask Purchase medical certificate.</p>
            <p>Attached: the generated medical certificate, the CPAP Usage Report, and the Sleep Study Report.</p>
          `,
          attachments: [
            {
              filename: certificateFileName,
              path: certificateUrl,
              contentType:
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            },
            usageAttachment,
            sleepStudyAttachment,
          ],
        },
      });
      doctorMailQueued = true;
      const sentCaseSnapshot = await getDoc(doc(db, linkedCase.refPath));
      if (sentCaseSnapshot.exists()) {
        const refreshed = { ...linkedCase, ...sentCaseSnapshot.data() };
        setLinkedCase(refreshed);
        replaceEventCase(refreshed);
      }

      setEditingLinkedCase(false);
      notifyTopRight(
        "Doctor email queued. The status will update when sending is confirmed.",
      );
    } catch (error) {
      console.error("Unable to send Annual Mask doctor email from follow-up", error);
      const replacingExistingCertificate =
        generatedCertificateRef &&
        linkedCase.generatedMedicalCertificate &&
        linkedCase.generatedMedicalCertificate.storagePath ===
          generatedCertificateRef.fullPath;
      if (
        generatedCertificateRef &&
        !doctorMailQueued &&
        !certificateSaveStarted &&
        !replacingExistingCertificate
      ) {
        try {
          await deleteObject(generatedCertificateRef);
        } catch (cleanupError) {
          console.warn("Unable to clean up generated medical certificate", cleanupError);
        }
      }
      notifyError(error.message || "Cannot send doctor email.");
    } finally {
      setSaving(false);
    }
  };

  const downloadDoctorPackage = async () => {
    if (!canManage || !linkedCase) {
      notifyWarning("You do not have permission to download this package.");
      return;
    }
    if (!linkedCase.usageReport) {
      notifyWarning("Upload a CPAP Usage Report before downloading the ZIP.");
      return;
    }
    const sleepStudyReport = getLinkedCaseSleepStudyReport();
    if (!sleepStudyReport) {
      notifyWarning("Select a Sleep Study Report before downloading the ZIP.");
      return;
    }

    setSaving(true);
    try {
      const certificateBlob = await generateLinkedCaseMedicalCertificate();
      const certificateFileName = getAnnualMaskMedicalCertFileName(
        linkedCase.patient || linkedCase,
      );
      const [usageReportBlob, sleepStudyReportBlob] = await Promise.all([
        readStoredPdf(linkedCase.usageReport, "CPAP Usage Report"),
        readStoredPdf(sleepStudyReport, "Sleep Study Report"),
      ]);

      await downloadAnnualMaskDoctorPackage({
        caseItem: linkedCase,
        certificateBlob,
        certificateFileName,
        usageReportBlob,
        usageReportFileName:
          linkedCase.usageReport.fileName || "CPAP-Usage-Report.pdf",
        sleepStudyReportBlob,
        sleepStudyReportFileName:
          sleepStudyReport.fileName || "Sleep-Study-Report.pdf",
      });
      notifyTopRight("Annual Mask Purchase doctor package downloaded.");
    } catch (error) {
      console.error("Unable to download Annual Mask doctor package", error);
      notifyError(error.message || "Cannot download the doctor package.");
    } finally {
      setSaving(false);
    }
  };

  const skipDoctorStep = async () => {
    if (!canManage || !linkedCase || saving || !canSkipAnnualMaskDoctorStep(linkedCase)) return;
    setSaving(true);
    try {
      await updateLinkedCaseWorkflow({ status: ANNUAL_MASK_STATUS.DOCTOR_STEP_SKIPPED });
      notifyTopRight("Certificate generation and doctor email skipped. Upload the signed certificate when ready.");
    } catch (error) {
      notifyError(error.message || "Cannot skip the doctor step.");
    } finally {
      setSaving(false);
    }
  };

  const skipSignedCertificateUpload = async () => {
    if (!canManage || !linkedCase || saving || !canSkipAnnualMaskSignedCertificate(linkedCase)) return;
    setSaving(true);
    try {
      const confirmed = await swal({
        title: "Skip signed certificate upload?",
        text: "This will skip uploading the signed medical certificate and allow you to continue to the Social Security result without a signed PDF. Confirm that you want to skip this step.",
        icon: "warning",
        buttons: ["Cancel", "Confirm skip"],
      });
      if (!confirmed) return;
      await updateLinkedCaseWorkflow({
        signedCertificateUploadSkip: {
          confirmed: true,
          skippedAt: serverTimestamp(),
          skippedBy: getActorName(currentUser),
        },
        status: ANNUAL_MASK_STATUS.SIGNED_CERTIFICATE_SKIPPED,
      });
      notifyTopRight("Signed certificate upload skipped. You can record the Social Security result.");
    } catch (error) {
      notifyError(error.message || "Cannot skip signed certificate upload.");
    } finally {
      setSaving(false);
    }
  };

  const chooseSignedCertificate = () => {
    if (!canUploadCertificate || !linkedCase || saving) return;
    if (
      getAnnualMaskSocialSecurityDecision(linkedCase) ===
        ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED ||
      isAnnualMaskPurchaseResultLocked(linkedCase)
    ) {
      notifyWarning(
        "Change the Social Security result to Not passed before replacing the signed certificate.",
      );
      return;
    }
    if (signedCertificateInputRef.current) {
      signedCertificateInputRef.current.click();
    }
  };

  const uploadSignedCertificate = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file || !linkedCase || saving) return;
    if (!canUploadCertificate || isAnnualMaskCertificateUploadLocked(linkedCase)) {
      notifyWarning("You cannot upload a certificate for this case in its current state.");
      return;
    }
    if (!isPdf(file)) {
      notifyWarning("Upload the signed medical certificate as a PDF file.");
      return;
    }

    setSaving(true);
    let uploadedCertificateRef = null;
    let certificateSaved = false;
    try {
      const pressureReview = await readAnnualMaskSignedPressure(file, linkedCase.certificateData);
      const savedFileName = `${Date.now()}_${safeStorageName(file.name)}`;
      const storagePath = `annual-mask-purchases/${linkedCase.hn}/${linkedCase.batchId}/${linkedCase.id}/signed-medical-certificate/${savedFileName}`;
      uploadedCertificateRef = ref(storage, storagePath);
      await uploadBytes(uploadedCertificateRef, file, {
        contentType: file.type || "application/pdf",
      });
      const url = await getDownloadURL(uploadedCertificateRef);
      let previousCertificate = linkedCase.signedMedicalCertificate;

      const values = {
        signedMedicalCertificate: {
          pressureReview,
          fileName: file.name,
          savedFileName,
          storagePath,
          url,
          contentType: file.type || "application/pdf",
          uploadedAt: new Date().toISOString(),
          uploadedBy: getActorName(currentUser),
        },
        signedCertificateUploadSkip: null,
        signedMedicalCertificateUploadedAt: serverTimestamp(),
        signedMedicalCertificateUploadedBy: getActorName(currentUser),
        socialSecurityDecision:
          ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PENDING,
        socialSecurityDecisionAt: null,
        socialSecurityDecisionBy: "",
        socialSecurityApproved: false,
        socialSecurityApprovedAt: null,
        socialSecurityApprovedBy: "",
        status: ANNUAL_MASK_STATUS.SIGNED_CERTIFICATE_UPLOADED,
      };
      if (physicianOnly) {
        previousCertificate = await saveAnnualMaskSignedCertificate({
          database: db,
          caseItem: linkedCase,
          currentUser,
          certificate: values.signedMedicalCertificate,
        });
        certificateSaved = true;
        const nextCase = { ...linkedCase, ...values };
        setLinkedCase(nextCase);
        replaceEventCase(nextCase);
        if (onCaseLinked && isCurrentFollowupLink(linkedCase)) {
          onCaseLinked({
            ...(annualLink || {}),
            batchId: nextCase.batchId,
            batchName: nextCase.batchName,
            caseId: nextCase.id,
            casePath: nextCase.refPath,
            status: nextCase.status,
            updatedAt: new Date().toISOString(),
          });
        }
      } else {
        await updateLinkedCaseWorkflow(values);
      }
      certificateSaved = true;
      if (
        previousCertificate &&
        previousCertificate.storagePath &&
        previousCertificate.storagePath !== storagePath
      ) {
        try {
          await deleteObject(ref(storage, previousCertificate.storagePath));
        } catch (cleanupError) {
          console.warn("Unable to remove replaced signed certificate", cleanupError);
        }
      }
      notifyTopRight("Signed medical certificate uploaded.");
    } catch (error) {
      console.error("Unable to upload signed certificate from follow-up", error);
      if (uploadedCertificateRef && !certificateSaved) {
        try {
          await deleteObject(uploadedCertificateRef);
        } catch (cleanupError) {
          console.warn("Unable to clean up signed certificate", cleanupError);
        }
      }
      notifyError(error.message || "Cannot upload signed medical certificate.");
    } finally {
      setSaving(false);
    }
  };

  const recordSocialSecurityDecision = async (decision) => {
    if (!canManage || !linkedCase) return;
    if (!isAnnualMaskSignedCertificateStepComplete(linkedCase)) {
      notifyWarning("Upload the signed medical certificate or confirm skipping this step first.");
      return;
    }
    if (isAnnualMaskPurchaseResultLocked(linkedCase)) {
      notifyWarning(
        "The Social Security result cannot change after the case is ready to purchase.",
      );
      return;
    }

    const passed =
      decision === ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED;
    const actor = getActorName(currentUser);
    setSaving(true);
    try {
      await updateLinkedCaseWorkflow({
        socialSecurityDecision: decision,
        socialSecurityDecisionAt: serverTimestamp(),
        socialSecurityDecisionBy: actor,
        socialSecurityApproved: passed,
        socialSecurityApprovedAt: passed ? serverTimestamp() : null,
        socialSecurityApprovedBy: passed ? actor : "",
        status: passed
          ? ANNUAL_MASK_STATUS.SOCIAL_SECURITY_APPROVED
          : ANNUAL_MASK_STATUS.SOCIAL_SECURITY_NOT_PASSED,
      });
      notifyTopRight(
        passed
          ? "Annual Mask Purchase passed by Social Security."
          : "Annual Mask Purchase marked as not passed by Social Security.",
      );
    } catch (error) {
      console.error("Unable to record Social Security result", error);
      notifyError("Cannot record the Social Security result.");
    } finally {
      setSaving(false);
    }
  };

  const selectAnnualMask = async (accessoryId) => {
    if (!canManage || !linkedCase) return;
    if (
      [
        ANNUAL_MASK_STATUS.ORDER_CREATED,
        ANNUAL_MASK_STATUS.ORDER_COMPLETED,
      ].includes(linkedCase.status) ||
      Boolean(linkedCase.saleOrderId)
    ) {
      notifyWarning("The mask cannot change after the Sale Order is created.");
      return;
    }
    if (
      getAnnualMaskSocialSecurityDecision(linkedCase) !==
      ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED
    ) {
      notifyWarning("Mark the case as passed by Social Security first.");
      return;
    }
    const mask = masks.find((item) => item.id === accessoryId);
    if (!mask) {
      notifyWarning("Select a valid mask inventory item.");
      return;
    }
    if (Number(mask.stockQuantity) <= 0) {
      notifyWarning("This mask is out of stock.");
      return;
    }

    setSaving(true);
    try {
      await updateLinkedCaseWorkflow({
        selectedMask: {
          accessoryId: mask.id,
          itemCode: mask.itemCode || "",
          itemName: mask.itemName || "",
          brand: mask.brand || "",
          size: mask.size || "",
          unitPrice: Number(mask.unitPrice) || 0,
          selectedAt: new Date().toISOString(),
          selectedBy: getActorName(currentUser),
        },
      });
      notifyTopRight("Mask type selected.");
    } catch (error) {
      console.error("Unable to select Annual Mask from follow-up", error);
      notifyError("Cannot select mask type.");
    } finally {
      setSaving(false);
    }
  };

  const selectAnnualSale = async (saleId) => {
    if (!canManage || !linkedCase) return;
    if (
      [
        ANNUAL_MASK_STATUS.ORDER_CREATED,
        ANNUAL_MASK_STATUS.ORDER_COMPLETED,
      ].includes(linkedCase.status) ||
      Boolean(linkedCase.saleOrderId)
    ) {
      notifyWarning(
        "The Sales Representative cannot change after the Sale Order is created.",
      );
      return;
    }
    const sale = sales.find((item) => item.id === saleId);
    if (!sale) {
      notifyWarning("Select an available Sales Representative.");
      return;
    }

    setSaving(true);
    try {
      await updateLinkedCaseWorkflow({
        selectedSale: {
          saleId: sale.id,
          name: safeText(sale.name),
          selectedAt: new Date().toISOString(),
          selectedBy: getActorName(currentUser),
        },
      });
      notifyTopRight("Sales Representative selected.");
    } catch (error) {
      console.error("Unable to select Annual Mask Sales Representative", error);
      notifyError("Cannot select the Sales Representative.");
    } finally {
      setSaving(false);
    }
  };

  const saveMaskReceiptDate = async (date) => {
    if (!canManage || saving || !linkedCase || linkedCase.saleOrderId ||
      [ANNUAL_MASK_STATUS.ORDER_CREATED, ANNUAL_MASK_STATUS.ORDER_COMPLETED].includes(linkedCase.status) ||
      !isAnnualMaskReceiptDateValid(date)) return;
    setSaving(true);
    try {
      await updateLinkedCaseWorkflow({ maskReceiptDate: date });
      notifyTopRight("Expected mask receipt date saved.");
    } catch (error) {
      notifyError("Cannot save the expected mask receipt date.");
    } finally {
      setSaving(false);
    }
  };

  const markReadyToPurchase = async () => {
    if (!canManage || !linkedCase) return;
    if (
      getAnnualMaskSocialSecurityDecision(linkedCase) !==
      ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED
    ) {
      notifyWarning("Mark the case as passed by Social Security first.");
      return;
    }
    if (!linkedCase.selectedMask || !linkedCase.selectedMask.accessoryId) {
      notifyWarning("Select the approved mask type before marking ready.");
      return;
    }

    if (!isAnnualMaskReceiptDateValid(linkedCase.maskReceiptDate) && !linkedCase.saleOrderId) {
      notifyWarning("Save the expected mask receipt date before marking ready.");
      return;
    }
    const selectedSaleId = getAnnualMaskSelectedSaleId(linkedCase);
    const selectedSaleName = getAnnualMaskSelectedSaleName(linkedCase);
    const selectedSale = sales.find((sale) => sale.id === selectedSaleId);
    if (!selectedSaleName || !selectedSale) {
      notifyWarning("Select a Sales Representative before marking ready.");
      return;
    }
    const selectedMask = masks.find(
      (mask) =>
        mask.id === safeText(linkedCase.selectedMask.accessoryId),
    );
    if (!selectedMask) {
      notifyWarning("The selected mask is no longer available in inventory.");
      return;
    }

    const confirmed = await swal({
      title: "Ready and create Sale Order?",
      text: `Reserve 1 x ${
        selectedMask.itemName || linkedCase.selectedMask.itemName
      } for ${getAnnualMaskOrderPatientLabel(
        linkedCase,
      )} and assign the order to ${selectedSaleName}.`,
      icon: "warning",
      buttons: true,
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      const orderResult = await createAnnualMaskSaleOrderWithStock({
        database: db,
        caseItem: linkedCase,
        mask: selectedMask,
        currentUser,
      });
      const saleOrderStatus =
        safeText(orderResult.orderData && orderResult.orderData.status) ||
        "เตรียมเครื่อง";
      await updateLinkedCaseWorkflow({
        saleOrderId: orderResult.orderRef.id,
        saleOrderReceiptNo: orderResult.receiptNo,
        saleOrderStatus,
        saleOrderCreatedAt:
          (orderResult.orderData && orderResult.orderData.createdAt) ||
          serverTimestamp(),
        saleOrderCreatedBy:
          safeText(
            orderResult.orderData && orderResult.orderData.createdBy,
          ) || getActorName(currentUser),
        status: getAnnualMaskCaseStatusForSaleOrder(saleOrderStatus),
      });
      notifyTopRight(
        orderResult.alreadyExisted
          ? `Existing Sale Order ${orderResult.receiptNo} linked to this case.`
          : `Annual Mask Purchase is ready. Sale Order ${orderResult.receiptNo} was created automatically.`,
      );
    } catch (error) {
      console.error(
        "Unable to mark Annual Mask ready and create Sale Order from follow-up",
        error,
      );
      notifyError(
        error.message ||
          "Cannot mark Annual Mask Purchase as ready or create the Sale Order.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePurchaseComplete = (values) => {
    const nextCase = { ...linkedCase, ...values };
    setLinkedCase(nextCase);
    replaceEventCase(nextCase);
    if (onCaseLinked && isCurrentFollowupLink(linkedCase)) {
      onCaseLinked({ ...(annualLink || {}), status: values.status, updatedAt: new Date().toISOString() });
    }
  };

  const fetchLatestClinicalValues = async () => {
    if (
      !selectedSleepStudyReport &&
      !therapyMinPressure &&
      !therapyMaxPressure
    ) {
      notifyWarning(
        "Add a Sleep Study Report or import Min/Max Pressure into Therapy CPAP first.",
      );
      return;
    }

    setFetchingClinicalData(true);
    let sleepStudyValues = {};
    let sleepStudyReadError = null;

    try {
      if (selectedSleepStudyReport) {
        try {
          const sleepStudyFile = await readStoredPdf(
            selectedSleepStudyReport,
            "Sleep Study Report",
          );
          sleepStudyValues = extractSleepStudyClinicalValues(
            await extractAnnualMaskPdfText(sleepStudyFile),
          );
        } catch (error) {
          sleepStudyReadError = error;
          console.warn("Unable to read selected Sleep Study Report", error);
        }
      }

      const changedFields = [
        therapyMinPressure && "Min Pressure",
        therapyMaxPressure && "Max Pressure",
        sleepStudyValues.ahi && "AHI",
        sleepStudyValues.minSpo2 && "Min SpO₂",
        sleepStudyValues.latestSleepStudyDate && "Sleep Study Date",
      ].filter(Boolean);
      setCreateForm((current) => ({
        ...current,
        ...(therapyMinPressure ? { minPressure: therapyMinPressure } : {}),
        ...(therapyMaxPressure ? { maxPressure: therapyMaxPressure } : {}),
        ...(sleepStudyValues.ahi ? { ahi: sleepStudyValues.ahi } : {}),
        ...(sleepStudyValues.minSpo2
          ? { minSpo2: sleepStudyValues.minSpo2 }
          : {}),
        ...(sleepStudyValues.latestSleepStudyDate
          ? { latestSleepStudyDate: sleepStudyValues.latestSleepStudyDate }
          : {}),
      }));

      if (changedFields.length) {
        notifyTopRight(
          `Fetched ${Array.from(new Set(changedFields)).join(", ")}. Review before creating the case.`,
        );
      }

      if (sleepStudyReadError) {
        notifyWarning(
          "The selected Sleep Study Report could not be read. Pressure was still taken from the current Therapy CPAP fields.",
        );
      } else if (!changedFields.length) {
        notifyWarning(
          "No supported AHI, Min SpO₂, Sleep Study Date, or Therapy Min/Max Pressure was found.",
        );
      }
    } finally {
      setFetchingClinicalData(false);
    }
  };

  const createAnnualMaskCase = async () => {
    if (!canCreate) {
      notifyWarning(
        "The CPAP follow-up event must exist before creating the Annual Mask Purchase.",
      );
      return;
    }
    const batchName = safeText(createForm.batchName);
    if (!createForm.batchId && !batchName) {
      notifyWarning("Enter a purchase batch name.");
      return;
    }
    if (!createForm.doctorName || !createForm.doctorEmail) {
      notifyWarning("Select a doctor with an email address.");
      return;
    }
    const localUsageFile = getAnnualMaskLocalUsageFile(
      createForm.usageFile,
      selectedUsageReport,
    );
    if (localUsageFile && !isPdf(localUsageFile)) {
      notifyWarning("Upload the CPAP Usage Report as a PDF file.");
      return;
    }
    if (!createForm.usageFile && !selectedUsageReport) {
      notifyWarning(
        "Select a CPAP Usage Report from Therapy Data or upload a PDF file.",
      );
      return;
    }
    if (
      !createForm.usageFile &&
      selectedUsageReport &&
      !selectedUsageReport.storagePath &&
      !selectedUsageReport.url &&
      !selectedUsageReport.localFile
    ) {
      notifyWarning("The selected CPAP Usage Report is unavailable.");
      return;
    }

    setSaving(true);
    let uploadedUsageRef = null;
    let createdBatchRef = null;
    let caseRef = null;
    let caseSaved = false;
    try {
      const selectedBatch = sortedBatches.find(
        (batch) => batch.id === createForm.batchId,
      );
      const batchRef = selectedBatch
        ? doc(db, ANNUAL_MASK_BATCHES_COLLECTION, selectedBatch.id)
        : doc(collection(db, ANNUAL_MASK_BATCHES_COLLECTION));
      const resolvedBatchName = safeText(
        (selectedBatch && selectedBatch.batchName) || batchName,
      );
      caseRef = doc(
        collection(
          db,
          ANNUAL_MASK_BATCHES_COLLECTION,
          batchRef.id,
          ANNUAL_MASK_CASES_COLLECTION,
        ),
      );
      const actor = getActorName(currentUser);
      let usageReport = null;

      if (localUsageFile) {
        const savedFileName = `${Date.now()}_${safeStorageName(
          localUsageFile.name,
        )}`;
        const storagePath = `annual-mask-purchases/${hn}/${batchRef.id}/${caseRef.id}/usage-report/${savedFileName}`;
        uploadedUsageRef = ref(storage, storagePath);
        await uploadBytes(uploadedUsageRef, localUsageFile, {
          contentType: localUsageFile.type || "application/pdf",
        });
        const usageReportUrl = await getDownloadURL(uploadedUsageRef);
        usageReport = {
          fileName: localUsageFile.name,
          savedFileName,
          storagePath,
          url: usageReportUrl,
          contentType: localUsageFile.type || "application/pdf",
          uploadedAt: new Date().toISOString(),
          uploadedBy: actor,
          source: createForm.usageFile
            ? "annual-mask-upload"
            : "cpap-followup-local",
          annualMaskOwned: true,
        };
      } else {
        usageReport = getSourceUsageReport(selectedUsageReport);
      }
      const sleepStudyReport = selectedSleepStudyReport
        ? getAnnualMaskSleepStudyReportReference(selectedSleepStudyReport)
        : null;

      if (!selectedBatch) {
        await setDoc(batchRef, {
          batchName: resolvedBatchName,
          createdAt: serverTimestamp(),
          createdBy: actor,
          updatedAt: serverTimestamp(),
          updatedBy: actor,
        });
        createdBatchRef = batchRef;
      } else {
        await setDoc(
          batchRef,
          {
            updatedAt: serverTimestamp(),
            updatedBy: actor,
          },
          { merge: true },
        );
      }

      const casePath = getAnnualMaskCasePath(batchRef.id, caseRef.id);
      const workflowLink = {
        batchId: batchRef.id,
        batchName: resolvedBatchName,
        caseId: caseRef.id,
        casePath,
        status: ANNUAL_MASK_STATUS.DRAFT,
        createdAt: new Date().toISOString(),
        createdBy: actor,
      };
      const patient = getFollowupPatient(followup);
      const certificatePressure = formatAnnualMaskPressureRange({
        minPressure: createForm.minPressure,
        maxPressure: createForm.maxPressure,
      });

      await setDoc(caseRef, {
        batchId: batchRef.id,
        batchName: resolvedBatchName,
        hn,
        patient,
        doctorId: createForm.doctorId,
        doctorName: createForm.doctorName,
        doctorEmail: createForm.doctorEmail,
        certificateData: {
          ahi: safeText(createForm.ahi),
          minSpo2: safeText(createForm.minSpo2),
          minPressure: safeText(createForm.minPressure),
          maxPressure: safeText(createForm.maxPressure),
          pressure: certificatePressure,
          latestSleepStudyDate: safeText(createForm.latestSleepStudyDate),
        },
        usageReport,
        sleepStudyReport,
        sourceFollowupId: followupId,
        sourceFollowupPath: `cpap-followup/${followupId}`,
        sourceFollowupDate: safeText(followup.date || followup.followupDate),
        sourceFollowupType: safeText(followup.type),
        socialSecurityDecision:
          ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PENDING,
        socialSecurityApproved: false,
        status: ANNUAL_MASK_STATUS.DRAFT,
        createdAt: serverTimestamp(),
        createdBy: actor,
        updatedAt: serverTimestamp(),
        updatedBy: actor,
      });
      caseSaved = true;

      await updateDoc(doc(db, "cpap-followup", followupId), {
        "extendedProps.annualMaskPurchase": workflowLink,
        updatedAt: serverTimestamp(),
        updatedBy: actor,
      });

      const nextCase = {
        id: caseRef.id,
        refPath: casePath,
        batchId: batchRef.id,
        batchName: resolvedBatchName,
        hn,
        patient,
        doctorId: createForm.doctorId,
        doctorName: createForm.doctorName,
        doctorEmail: createForm.doctorEmail,
        certificateData: {
          ahi: safeText(createForm.ahi),
          minSpo2: safeText(createForm.minSpo2),
          minPressure: safeText(createForm.minPressure),
          maxPressure: safeText(createForm.maxPressure),
          pressure: certificatePressure,
          latestSleepStudyDate: safeText(createForm.latestSleepStudyDate),
        },
        usageReport,
        sleepStudyReport,
        sourceFollowupId: followupId,
        sourceFollowupPath: `cpap-followup/${followupId}`,
        sourceFollowupDate: safeText(followup.date || followup.followupDate),
        sourceFollowupType: safeText(followup.type),
        socialSecurityDecision:
          ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PENDING,
        socialSecurityApproved: false,
        status: ANNUAL_MASK_STATUS.DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setLinkedCase(nextCase);
      setEventCases((current) =>
        sortCasesNewest([
          nextCase,
          ...current.filter(
            (caseItem) => caseItem.refPath !== nextCase.refPath,
          ),
        ]),
      );
      if (!selectedBatch) {
        setBatches((current) => [
          {
            id: batchRef.id,
            batchName: resolvedBatchName,
            createdAt: new Date().toISOString(),
            createdBy: actor,
          },
          ...current,
        ]);
      }
      setCreateForm(makeLinkedCaseForm(nextCase, followup));
      setCreatingAnotherCase(false);
      if (onCaseLinked) onCaseLinked(workflowLink);
      notifyTopRight("Annual Mask Purchase is linked to this CPAP follow-up event.");
    } catch (error) {
      console.error("Unable to create Annual Mask Purchase from follow-up", error);
      if (caseSaved && caseRef) {
        try {
          await deleteDoc(caseRef);
        } catch (cleanupError) {
          console.warn("Unable to clean up Annual Mask Purchase case", cleanupError);
        }
      }
      if (uploadedUsageRef) {
        try {
          await deleteObject(uploadedUsageRef);
        } catch (cleanupError) {
          console.warn("Unable to clean up Annual Mask Usage Report", cleanupError);
        }
      }
      if (createdBatchRef) {
        try {
          await deleteDoc(createdBatchRef);
        } catch (cleanupError) {
          console.warn("Unable to clean up empty Annual Mask batch", cleanupError);
        }
      }
      notifyError(error.message || "Cannot create Annual Mask Purchase.");
    } finally {
      setSaving(false);
    }
  };

  const renderPdfPicker = ({
    label,
    reports,
    selectedKey,
    onSelect,
    emptyText,
    helperText,
  }) => (
    <Form.Group>
      <Form.Label>{label}</Form.Label>
      {reports.length > 0 ? (
        <div
          style={{
            border: "1px solid #dee2e6",
            borderRadius: 6,
            overflow: "hidden",
            maxHeight: 190,
            overflowY: "auto",
          }}
        >
          {reports.map((file, index) => {
            const fileKey = getAnnualMaskFileKey(file, index);
            const isSelected = fileKey === selectedKey;
            const isPending = Boolean(file.pendingUpload);
            const reportDate =
              file.queuedAt || file.localImportedAt || file.uploadedAt;

            return (
              <button
                key={fileKey}
                type="button"
                onClick={() => onSelect(fileKey)}
                disabled={!canCreate || saving || fetchingClinicalData}
                aria-pressed={isSelected}
                style={{
                  width: "100%",
                  border: "none",
                  borderBottom:
                    index === reports.length - 1
                      ? "none"
                      : "1px solid #edf0f2",
                  background: isSelected ? "#edf7ff" : "#fff",
                  padding: "8px 10px",
                  display: "grid",
                  gridTemplateColumns: "20px minmax(0, 1fr) auto 16px",
                  alignItems: "center",
                  gap: 8,
                  textAlign: "left",
                  color: "inherit",
                }}
              >
                <i
                  className="flaticon-381-file-2"
                  style={{ color: "#dd0102", fontSize: 15 }}
                />
                <span style={{ minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: 12,
                      lineHeight: 1.25,
                    }}
                    title={getReportName(file)}
                  >
                    {index === 0 ? "Latest · " : ""}
                    {getReportName(file)}
                  </span>
                  <small
                    className="text-muted"
                    style={{ display: "block", fontSize: 10, marginTop: 2 }}
                  >
                    {isPending ? "Added locally" : "Uploaded"}: {toTimestampText(reportDate)}
                  </small>
                </span>
                <Badge variant={isPending ? "warning" : "success"}>
                  {isPending ? "Waiting to save" : "Saved"}
                </Badge>
                <i
                  className={
                    isSelected
                      ? "fa fa-check-circle text-primary"
                      : "fa fa-circle-o text-muted"
                  }
                />
              </button>
            );
          })}
        </div>
      ) : (
        <div className="border rounded px-2 py-2 text-muted small">
          {emptyText}
        </div>
      )}
      {helperText && <small className="form-text text-muted">{helperText}</small>}
    </Form.Group>
  );

  const renderEventBatchPicker = () => {
    if (!eventCases.length) return null;

    return (
      <div className="border rounded p-3 mb-3" style={{ background: "#f8fafc" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
          <div>
            <div className="font-weight-bold">
              Batches in this CPAP follow-up event
            </div>
            <small className="text-muted">
              {eventCases.length} Annual Mask Purchase {eventCases.length === 1 ? "batch" : "batches"}
            </small>
          </div>
          {canManage && !creatingAnotherCase && (
            <Button
              type="button"
              size="sm"
              variant="primary"
              className="mt-2 mt-md-0"
              onClick={beginAnotherCase}
              disabled={saving}
            >
              <i className="fa fa-plus mr-1" /> Create another batch
            </Button>
          )}
        </div>
        <Form.Control
          as="select"
          size="sm"
          value={creatingAnotherCase ? "" : safeText(linkedCase && linkedCase.refPath)}
          disabled={saving}
          onChange={(event) => selectEventCase(event.target.value)}
        >
          {creatingAnotherCase && <option value="">Creating a new batch…</option>}
          {eventCases.map((caseItem) => {
            const caseStatus = getAnnualMaskStatusConfig(caseItem.status);
            return (
              <option key={caseItem.refPath} value={caseItem.refPath}>
                {caseItem.batchName || caseItem.batchId || "Unnamed batch"} — {caseStatus.label} — {toTimestampText(caseItem.createdAt)}
              </option>
            );
          })}
        </Form.Control>
        <small className="d-block text-muted mt-2">
          Select a batch to review its files, workflow status, mask, and sale order.
        </small>
      </div>
    );
  };

  const renderCaseFiles = (caseItem) => (
    <div className="mb-2">
      <div className="small text-muted mb-1">Case files</div>
      {[
        ["Sleep Study Report", caseItem.sleepStudyReport],
        ["CPAP Usage Report", caseItem.usageReport],
        ["Generated Form", caseItem.generatedMedicalCertificate],
        ["Signed medical certificate", caseItem.signedMedicalCertificate],
      ].map(([label, file]) => file ? (
        <Button
          key={label}
          type="button"
          size="sm"
          variant="outline-primary"
          className="mr-2 mb-2 text-left"
          style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
          onClick={() => openFilePreview(file, label)}
        >
          <i className="fa fa-file-pdf-o mr-1" />
          {label}: {getReportName(file)}
        </Button>
      ) : (
        <span key={label} className="d-inline-block text-muted small mr-3 mb-2">No {label}</span>
      ))}
    </div>
  );

  if (!followupId) {
    return (
      <Card className="border-0 bg-light">
        <Card.Body>
          <h6 className="mb-1">Annual Mask Purchase</h6>
          <div className="text-muted small">
            Save this CPAP follow-up event first. The Annual Mask Purchase will then remain linked to this exact follow-up event.
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="border rounded px-3 py-2 text-muted small">
        Loading Annual Mask Purchase…
      </div>
    );
  }

  if (readOnly) {
    return (
      <>
        <Card className="border mb-0 shadow-sm">
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap py-3 px-3 bg-white">
            <div className="d-flex align-items-center mr-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white mr-2"
                style={{ width: 32, height: 32, flex: "0 0 32px" }}
              >
                <i className="fa fa-shopping-bag" />
              </div>
              <div>
                <div className="d-flex align-items-center">
                  <strong style={{ fontSize: 15 }}>Annual Mask Purchase</strong>
                  <Badge variant="primary" pill className="ml-2">
                    {eventCases.length}
                  </Badge>
                </div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  Review each batch and its current purchase workflow.
                </div>
              </div>
            </div>
            <Link
              to="/annual-mask-purchase"
              className="btn btn-sm btn-outline-primary mt-1 mb-1"
            >
              Review all batches
            </Link>
          </Card.Header>
          <Card.Body className="p-3">
            {eventCases.length ? (
              <div style={{ maxHeight: 360, overflowY: "auto", paddingRight: 4 }}>
                {eventCases.map((caseItem, index) => {
                  const caseStatus = getAnnualMaskStatusConfig(caseItem.status);
                  const maskName = safeText(
                    caseItem.selectedMask && caseItem.selectedMask.itemName,
                  );
                  const maskSize = safeText(
                    caseItem.selectedMask && caseItem.selectedMask.size,
                  );
                  const saleName = getAnnualMaskSelectedSaleName(caseItem);
                  const orderNumber = safeText(caseItem.saleOrderReceiptNo);
                  const orderStatus = safeText(caseItem.saleOrderStatus);
                  const socialSecurityDecision =
                    getAnnualMaskSocialSecurityDecision(caseItem);

                  return (
                    <div
                      key={caseItem.refPath}
                      className={`border rounded ${
                        index < eventCases.length - 1 ? "mb-3" : ""
                      }`}
                      style={{ overflow: "hidden" }}
                    >
                      <div
                        className="d-flex justify-content-between align-items-start px-3 py-2 border-bottom"
                        style={{ backgroundColor: "#f7f9fc", gap: 12 }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            className="font-weight-bold"
                            style={{ fontSize: 14, overflowWrap: "anywhere" }}
                            title={caseItem.batchName || caseItem.batchId}
                          >
                            {caseItem.batchName ||
                              caseItem.batchId ||
                              "Unnamed batch"}
                          </div>
                          <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                            <i className="fa fa-calendar-o mr-1" />
                            Created {toTimestampText(caseItem.createdAt)}
                          </div>
                        </div>
                        <Badge
                          variant={caseStatus.badge}
                          className="px-2 py-1"
                          style={{ whiteSpace: "nowrap", fontSize: 11 }}
                        >
                          {caseStatus.label}
                        </Badge>
                      </div>

                      <div className="px-3 pt-3 pb-3">
                        {renderCaseFiles(caseItem)}
                        <AnnualMaskTestEmailButton caseItem={caseItem} disabled={saving} />
                        <AnnualMaskPressureNotice certificate={caseItem.signedMedicalCertificate} />
                        <div
                          className="font-weight-bold text-dark mb-2"
                          style={{ fontSize: 12 }}
                        >
                          Workflow review
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(180px, 1fr))",
                            gap: 8,
                          }}
                        >
                          <AnnualMaskReviewField label="Doctor email">
                            <AnnualMaskDoctorEmailStatus caseItem={caseItem} />
                          </AnnualMaskReviewField>

                          <AnnualMaskReviewField label="Signed certificate">
                            <div
                              className={
                                caseItem.signedMedicalCertificate
                                  ? "font-weight-bold text-success"
                                  : "text-muted"
                              }
                            >
                              {caseItem.signedMedicalCertificate
                                ? "Uploaded"
                                : "Waiting"}
                            </div>
                            {caseItem.signedMedicalCertificate && (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 mt-1 text-left"
                                style={{ fontSize: 11, lineHeight: 1.35 }}
                                title={
                                  caseItem.signedMedicalCertificate.fileName ||
                                  "Signed medical certificate"
                                }
                                onClick={() =>
                                  openFilePreview(
                                    caseItem.signedMedicalCertificate,
                                    "Signed medical certificate",
                                  )
                                }
                              >
                                <i className="fa fa-eye mr-1" />
                                Review {caseItem.signedMedicalCertificate.fileName || "PDF"}
                              </button>
                            )}
                          </AnnualMaskReviewField>

                          <AnnualMaskReviewField label="Social Security">
                            <div
                              className={
                                socialSecurityDecision ===
                                ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED
                                  ? "font-weight-bold text-success"
                                  : socialSecurityDecision ===
                                    ANNUAL_MASK_SOCIAL_SECURITY_DECISION.NOT_PASSED
                                  ? "font-weight-bold text-danger"
                                  : "text-muted"
                              }
                            >
                              {socialSecurityDecision ===
                              ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED
                                ? "Passed"
                                : socialSecurityDecision ===
                                  ANNUAL_MASK_SOCIAL_SECURITY_DECISION.NOT_PASSED
                                ? "Not passed"
                                : "Waiting"}
                            </div>
                          </AnnualMaskReviewField>

                          <AnnualMaskReviewField label="Mask / size" highlight>
                            <div
                              className={
                                maskName
                                  ? "font-weight-bold text-dark"
                                  : "text-muted"
                              }
                            >
                              {maskName || "Not selected"}
                            </div>
                            <Badge
                              variant={maskSize ? "info" : "light"}
                              className="border mt-1 px-2 py-1"
                            >
                              Size: {maskSize || "Not recorded"}
                            </Badge>
                          </AnnualMaskReviewField>

                          <AnnualMaskReviewField label="Sales">
                            <div
                              className={
                                saleName
                                  ? "font-weight-bold text-success"
                                  : "text-muted"
                              }
                            >
                              {saleName || "Not selected"}
                            </div>
                          </AnnualMaskReviewField>

                          <AnnualMaskReviewField label="Expected mask receipt">
                            <strong>{caseItem.maskReceiptDate || "Not scheduled"}</strong>
                          </AnnualMaskReviewField>

                          <AnnualMaskReviewField label="Sale order">
                            <div
                              className={
                                orderNumber
                                  ? "font-weight-bold text-primary"
                                  : "text-muted"
                              }
                            >
                              {orderNumber || "Not created"}
                            </div>
                            {orderNumber && orderStatus && (
                              <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                                Status: {orderStatus}
                              </div>
                            )}
                          </AnnualMaskReviewField>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center px-3 py-4">
                <i
                  className="fa fa-shopping-bag text-muted mb-2"
                  style={{ fontSize: 22 }}
                />
                <div className="font-weight-bold" style={{ fontSize: 13 }}>
                  No Annual Mask Purchase batch
                </div>
                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                  This follow-up event does not have a mask purchase batch yet.
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
        {renderFilePreviewModal()}
      </>
    );
  }

  if (linkedCase && !creatingAnotherCase) {
    const statusConfig = getAnnualMaskStatusConfig(linkedCase.status);
    const certificateData = linkedCase.certificateData || {};
    const draftIsEditable = isAnnualMaskDraftEditable(linkedCase);
    const selectedMaskInventory = masks.find(
      (mask) =>
        mask.id ===
        safeText(
          linkedCase.selectedMask && linkedCase.selectedMask.accessoryId,
        ),
    );
    const purchaseIsReady = [
      ANNUAL_MASK_STATUS.READY_TO_PURCHASE,
      ANNUAL_MASK_STATUS.ORDER_CREATED,
      ANNUAL_MASK_STATUS.ORDER_COMPLETED,
    ].includes(linkedCase.status);
    const saleOrderIsLocked =
      [
        ANNUAL_MASK_STATUS.ORDER_CREATED,
        ANNUAL_MASK_STATUS.ORDER_COMPLETED,
      ].includes(linkedCase.status) || Boolean(linkedCase.saleOrderId);
    const socialSecurityDecision =
      getAnnualMaskSocialSecurityDecision(linkedCase);
    const socialSecurityPassed =
      socialSecurityDecision ===
      ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED;
    const socialSecurityNotPassed =
      socialSecurityDecision ===
      ANNUAL_MASK_SOCIAL_SECURITY_DECISION.NOT_PASSED;
    const selectedSaleId = getAnnualMaskSelectedSaleId(linkedCase);
    const selectedSaleName = getAnnualMaskSelectedSaleName(linkedCase);
    const selectedSaleIsAvailable = sales.some(
      (sale) => sale.id === selectedSaleId,
    );
    return (
      <>
        <Card className="border-primary">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <strong>Annual Mask Purchase Batches</strong>
            <div className="small text-muted">
              Review every Annual Mask Purchase batch created from this CPAP follow-up event.
            </div>
          </div>
          <div className="d-flex align-items-center mt-2 mt-md-0">
            <Badge variant={statusConfig.badge}>{statusConfig.label}</Badge>
            {canManage && draftIsEditable && !editingLinkedCase && (
              <Button
                size="sm"
                variant="outline-primary"
                className="ml-2"
                onClick={beginLinkedCaseEdit}
                disabled={saving}
              >
                <i className="fa fa-pencil mr-1" /> Edit batch
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {renderEventBatchPicker()}
          {editingLinkedCase ? (
            <Form
              onSubmit={(event) => {
                event.preventDefault();
                saveLinkedCaseChanges();
              }}
            >
              <div className="alert alert-info py-2 small">
                Review and update this draft before sending it to the doctor.
              </div>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Batch name</Form.Label>
                    <Form.Control
                      value={createForm.batchName}
                      disabled={saving}
                      onChange={(event) =>
                        updateCreateField("batchName", event.target.value)
                      }
                    />
                    <Form.Text className="text-muted">
                      Patient name remains part of the default batch name.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Doctor</Form.Label>
                    <Form.Control
                      as="select"
                      value={createForm.doctorId}
                      disabled={saving}
                      onChange={(event) => selectDoctor(event.target.value)}
                    >
                      <option value="">Select doctor</option>
                      {doctors.map((doctor) => (
                        <option key={doctor.id} value={doctor.id}>
                          {doctor.name || doctor.doctorName || doctor.id}
                          {doctor.email ? ` — ${doctor.email}` : " (no email)"}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>

              <div className="border rounded p-3 mb-3" style={{ background: "#f8fafc" }}>
                <div className="d-flex justify-content-between align-items-start flex-wrap mb-3">
                  <div>
                    <div style={{ fontWeight: 700 }}>Medical certificate values</div>
                    <small className="text-muted">
                      These saved values are used when the medical certificate is generated.
                    </small>
                  </div>
                  <div className="small text-muted mt-1">
                    Pressure range: <strong>{formatAnnualMaskPressureRange({
                      minPressure: createForm.minPressure,
                      maxPressure: createForm.maxPressure,
                    }) || "-"}</strong>{" "}
                    cmH₂O
                  </div>
                </div>
                <div className="font-weight-bold mb-2">1. Sleep Study Report</div>
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>AHI</Form.Label>
                      <Form.Control
                        type="number"
                        step="any"
                        value={createForm.ahi}
                        disabled={saving}
                        onChange={(event) => updateCreateField("ahi", event.target.value)}
                      />
                      <Form.Text className="text-muted">Sleep Study · events/hour</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Min SpO₂</Form.Label>
                      <Form.Control
                        type="number"
                        step="any"
                        value={createForm.minSpo2}
                        disabled={saving}
                        onChange={(event) =>
                          updateCreateField("minSpo2", event.target.value)
                        }
                      />
                      <Form.Text className="text-muted">Sleep Study · %</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Sleep Study Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={createForm.latestSleepStudyDate}
                        disabled={saving}
                        onChange={(event) =>
                          updateCreateField(
                            "latestSleepStudyDate",
                            event.target.value,
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="border-top pt-3 mb-2">
                  <div className="font-weight-bold">2. CPAP Usage — Min/Max Pressure</div>
                  <small className="text-muted">Pressure values from Therapy CPAP.</small>
                </div>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-md-0">
                      <Form.Label>Min Pressure</Form.Label>
                      <Form.Control
                        type="number"
                        step="any"
                        value={createForm.minPressure}
                        disabled={saving}
                        onChange={(event) =>
                          updateCreateField("minPressure", event.target.value)
                        }
                      />
                      <Form.Text className="text-muted">Therapy CPAP · cmH₂O</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-0">
                      <Form.Label>Max Pressure</Form.Label>
                      <Form.Control
                        type="number"
                        step="any"
                        value={createForm.maxPressure}
                        disabled={saving}
                        onChange={(event) =>
                          updateCreateField("maxPressure", event.target.value)
                        }
                      />
                      <Form.Text className="text-muted">Therapy CPAP · cmH₂O</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <Form.Group>
                <Form.Label>Replace CPAP Usage Report (PDF, optional)</Form.Label>
                <Form.Control
                  type="file"
                  accept="application/pdf,.pdf"
                  disabled={saving}
                  onChange={(event) =>
                    updateCreateField("usageFile", event.target.files[0] || null)
                  }
                />
                <Form.Text className="text-muted">
                  Leave empty to keep {linkedCase.usageReport
                    ? getReportName(linkedCase.usageReport)
                    : "the current report"}.
                </Form.Text>
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button
                  type="button"
                  size="sm"
                  variant="outline-secondary"
                  className="mr-2"
                  onClick={cancelLinkedCaseEdit}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={saving}>
                  {saving ? "Saving…" : "Save batch changes"}
                </Button>
              </div>
            </Form>
          ) : (
            <>
              <Row>
                <Col md={6} className="mb-3">
                  <div className="small text-muted">Batch</div>
                  <div className="font-weight-bold">
                    {linkedCase.batchName || linkedCase.batchId || "-"}
                  </div>
                  <small className="text-muted">ID: {linkedCase.batchId || "-"}</small>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="small text-muted">Patient</div>
                  <div>
                    {getPatientName(linkedCase.patient || followup) || "-"}
                  </div>
                  <small className="text-muted">HN: {linkedCase.hn || hn || "-"}</small>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="small text-muted">Doctor</div>
                  <div>{linkedCase.doctorName || "-"}</div>
                  <small className="text-muted">{linkedCase.doctorEmail || ""}</small>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="small text-muted">Sales Representative</div>
                  <div>{selectedSaleName || "-"}</div>
                  <small className="text-muted">
                    Required before Ready to Purchase
                  </small>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="small text-muted">Created / last updated</div>
                  <div>{toTimestampText(linkedCase.createdAt)}</div>
                  <small className="text-muted">
                    Updated: {toTimestampText(linkedCase.updatedAt)}
                  </small>
                </Col>
              </Row>

              <div className="border rounded p-3 mb-3" style={{ background: "#f8fafc" }}>
                <div className="font-weight-bold mb-2">Medical certificate values</div>
                <div className="font-weight-bold mb-2">1. Sleep Study Report</div>
                <Row>
                  <Col xs={6} md className="mb-2">
                    <div className="small text-muted">AHI</div>
                    <div>{formatClinicalValue(certificateData.ahi)}</div>
                  </Col>
                  <Col xs={6} md className="mb-2">
                    <div className="small text-muted">Min SpO₂</div>
                    <div>{formatClinicalValue(certificateData.minSpo2, "%")}</div>
                  </Col>
                  <Col xs={6} md className="mb-2">
                    <div className="small text-muted">Study date</div>
                    <div>{safeText(certificateData.latestSleepStudyDate) || "-"}</div>
                  </Col>
                </Row>
                <div className="border-top pt-3 mb-2">
                  <div className="font-weight-bold">2. CPAP Usage — Min/Max Pressure</div>
                </div>
                <Row>
                  <Col xs={6} className="mb-2">
                    <div className="small text-muted">Min pressure</div>
                    <div>
                      {formatClinicalValue(certificateData.minPressure, "cmH₂O")}
                    </div>
                  </Col>
                  <Col xs={6} className="mb-2">
                    <div className="small text-muted">Max pressure</div>
                    <div>
                      {formatClinicalValue(certificateData.maxPressure, "cmH₂O")}
                    </div>
                  </Col>
                </Row>
                <AnnualMaskPressureNotice certificate={linkedCase.signedMedicalCertificate} />
              </div>

              <Row>
                <Col md={6} className="mb-3">
                  {renderCaseFiles(linkedCase)}
                </Col>
                <Col md={6} className="mb-3">
                  <div className="small text-muted mb-1">Workflow review</div>
                  <AnnualMaskDoctorEmailStatus caseItem={linkedCase} onChange={(fields) => {
                      setLinkedCase((current) => current && current.refPath === linkedCase.refPath ? { ...current, ...fields } : current);
                      setEventCases((rows) => rows.map((item) => item.refPath === linkedCase.refPath ? { ...item, ...fields } : item));
                    }} />
                  <div className="small">
                    Signed certificate: {linkedCase.signedMedicalCertificate ? (
                      <span className="text-success">Uploaded</span>
                    ) : isAnnualMaskSignedCertificateStepComplete(linkedCase) ? (
                      <span className="text-muted">Skipped by confirmation</span>
                    ) : (
                      <span className="text-muted">Waiting</span>
                    )}
                  </div>
                  <div className="small">
                    Social Security: {socialSecurityPassed ? (
                      <span className="text-success">Passed</span>
                    ) : socialSecurityNotPassed ? (
                      <span className="text-danger">Not passed</span>
                    ) : (
                      <span className="text-muted">Waiting</span>
                    )}
                  </div>
                  <div className="small">
                    Mask: {safeText(
                      linkedCase.selectedMask && linkedCase.selectedMask.itemName,
                    ) || <span className="text-muted">Not selected</span>}
                  </div>
                  <div className="small">
                    Sales: {selectedSaleName || (
                      <span className="text-muted">Not selected</span>
                    )}
                  </div>
                  <div className="small">
                    <AnnualMaskReceiptDateText date={linkedCase.maskReceiptDate} />
                  </div>
                  <div className="small">
                    Sale order: {linkedCase.saleOrderReceiptNo ? (
                      `${linkedCase.saleOrderReceiptNo} · ${
                        linkedCase.saleOrderStatus || "-"
                      }`
                    ) : (
                      <span className="text-muted">Not created</span>
                    )}
                  </div>
                </Col>
              </Row>

              <AnnualMaskWorkflowProgress caseItem={linkedCase} />
              <AnnualMaskTestEmailButton caseItem={linkedCase} disabled={saving} />

              {physicianOnly && !canManage && canUploadCertificate && (
                <div className="border rounded p-3 mb-3">
                  <div className="font-weight-bold mb-2">Signed medical certificate</div>
                  <input
                    ref={signedCertificateInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="d-none"
                    onChange={uploadSignedCertificate}
                  />
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={chooseSignedCertificate}
                    disabled={saving || isAnnualMaskCertificateUploadLocked(linkedCase)}
                  >
                    <i className="fa fa-upload mr-1" />
                    {linkedCase.signedMedicalCertificate ? "Replace signed certificate" : "Upload signed certificate"}
                  </Button>
                  {isAnnualMaskCertificateUploadLocked(linkedCase) && (
                    <small className="d-block text-muted mt-2">The case has advanced. Ask an administrator before replacing its certificate.</small>
                  )}
                </div>
              )}

              {canManage && (
                <div className="border rounded p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                    <div>
                      <div className="font-weight-bold">Workflow actions</div>
                      <small className="text-muted">
                        Complete this patient’s Annual Mask Purchase without leaving the CPAP follow-up event.
                      </small>
                    </div>
                    <Badge variant={statusConfig.badge} className="mt-1">
                      {statusConfig.label}
                    </Badge>
                  </div>

                  <input
                    ref={signedCertificateInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="d-none"
                    onChange={uploadSignedCertificate}
                  />

                  <div className="border-top pt-3 mb-3">
                    <Row className="align-items-center">
                      <Col md={5} className="mb-2">
                        <div className="font-weight-bold annual-mask-step-heading">1. Send to doctor <AnnualMaskStepStatus caseItem={linkedCase} step="doctor" /></div>
                        <small className="text-muted">
                          Email all three files, download the ZIP, or skip generation and doctor email to upload your node's signed certificate later.
                        </small>
                      </Col>
                      <Col md={7} className="mb-2">
                        <Button
                          size="sm"
                          variant="outline-info"
                          className="mr-2 mb-1"
                          onClick={sendDoctorEmail}
                          disabled={
                            saving ||
                            !canGenerateAnnualMaskMedicalCert(linkedCase) ||
                            !linkedCase.doctorEmail ||
                            !linkedCase.usageReport ||
                            (!linkedCase.sleepStudyReport &&
                              !selectedSleepStudyReport)
                          }
                        >
                          <i className="fa fa-send mr-1" />
                          {linkedCase.doctorEmailSent
                            ? "Resend email to doctor"
                            : "Send email to doctor"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="mr-2 mb-1"
                          onClick={downloadDoctorPackage}
                          disabled={
                            saving ||
                            !canGenerateAnnualMaskMedicalCert(linkedCase) ||
                            !linkedCase.usageReport ||
                            (!linkedCase.sleepStudyReport &&
                              !selectedSleepStudyReport)
                          }
                        >
                          <i className="fa fa-download mr-1" />
                          Download ZIP
                        </Button>
                        <Button
                            size="sm"
                            variant="outline-primary"
                            className="mr-2 mb-1"
                            onClick={skipDoctorStep}
                            disabled={saving || !canSkipAnnualMaskDoctorStep(linkedCase)}
                            title={getAnnualMaskDoctorSkipUnavailableReason(linkedCase)}
                          >
                            Skip generation &amp; doctor email
                          </Button>
                        {!canSkipAnnualMaskDoctorStep(linkedCase) && (
                          <small className="d-block text-muted mb-1">{getAnnualMaskDoctorSkipUnavailableReason(linkedCase)}</small>
                        )}
                        {!canGenerateAnnualMaskMedicalCert(linkedCase) && (
                          <small className="d-block text-muted mb-1">
                            Use your node's own template and upload the signed PDF.
                          </small>
                        )}
                        <AnnualMaskDoctorEmailStatus caseItem={linkedCase} onChange={(fields) => {
                      setLinkedCase((current) => current && current.refPath === linkedCase.refPath ? { ...current, ...fields } : current);
                      setEventCases((rows) => rows.map((item) => item.refPath === linkedCase.refPath ? { ...item, ...fields } : item));
                    }} />
                      </Col>
                    </Row>
                  </div>

                  <div className="border-top pt-3 mb-3">
                    <Row className="align-items-center">
                      <Col md={5} className="mb-2">
                        <div className="font-weight-bold annual-mask-step-heading">2. Signed medical certificate <AnnualMaskStepStatus caseItem={linkedCase} step="certificate" /></div>
                        <small className="text-muted">
                          Upload the signed PDF, including certificates from your node's own template.
                        </small>
                      </Col>
                      <Col md={7} className="mb-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={chooseSignedCertificate}
                          disabled={
                            saving ||
                            socialSecurityPassed ||
                            purchaseIsReady ||
                            Boolean(linkedCase.saleOrderId)
                          }
                        >
                          <i className="fa fa-upload mr-1" />
                          {linkedCase.signedMedicalCertificate
                            ? "Replace signed certificate"
                            : "Upload signed certificate"}
                        </Button>
                        {canSkipAnnualMaskSignedCertificate(linkedCase) && (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="ml-2 mb-1"
                            disabled={saving}
                            onClick={skipSignedCertificateUpload}
                          >
                            Skip signed certificate upload
                          </Button>
                        )}
                        {linkedCase.signedCertificateUploadSkip && linkedCase.signedCertificateUploadSkip.confirmed && !linkedCase.signedMedicalCertificate && (
                          <small className="d-block text-muted mt-1">Signed certificate upload skipped by confirmation.</small>
                        )}

                        {linkedCase.signedMedicalCertificate && (
                          <div className="mt-2">
                            <small
                              className="text-success mr-2"
                              title={linkedCase.signedMedicalCertificate.fileName || ""}
                            >
                              <i className="fa fa-file-pdf-o mr-1" />
                              {linkedCase.signedMedicalCertificate.fileName ||
                                "Signed medical certificate.pdf"}
                            </small>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-success"
                              onClick={() =>
                                openFilePreview(
                                  linkedCase.signedMedicalCertificate,
                                  "Signed medical certificate",
                                )
                              }
                            >
                              <i className="fa fa-search mr-1" /> Review
                            </Button>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>

                  <div className="border-top pt-3 mb-3">
                    <Row>
                      <Col md={5} className="mb-2">
                        <div className="font-weight-bold annual-mask-step-heading">3. Social Security <AnnualMaskStepStatus caseItem={linkedCase} step="socialSecurity" /></div>
                        <small className="text-muted">
                          Send the files outside this system, then record only whether the case passed.
                        </small>
                      </Col>
                      <Col md={7} className="mb-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={socialSecurityPassed ? "success" : "outline-success"}
                          className="mr-2 mb-1"
                          disabled={
                            saving ||
                            !isAnnualMaskSignedCertificateStepComplete(linkedCase) ||
                            purchaseIsReady ||
                            Boolean(linkedCase.saleOrderId)
                          }
                          onClick={() =>
                            recordSocialSecurityDecision(
                              ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED,
                            )
                          }
                        >
                          <i className="fa fa-check-circle mr-1" /> Passed
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={socialSecurityNotPassed ? "danger" : "outline-danger"}
                          className="mb-1"
                          disabled={
                            saving ||
                            !isAnnualMaskSignedCertificateStepComplete(linkedCase) ||
                            purchaseIsReady ||
                            Boolean(linkedCase.saleOrderId)
                          }
                          onClick={() =>
                            recordSocialSecurityDecision(
                              ANNUAL_MASK_SOCIAL_SECURITY_DECISION.NOT_PASSED,
                            )
                          }
                        >
                          <i className="fa fa-times-circle mr-1" /> Not passed
                        </Button>
                        {socialSecurityDecision ===
                          ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PENDING && (
                          <small className="d-block text-muted mt-1">
                            Waiting for Social Security result
                          </small>
                        )}
                        {socialSecurityDecision !==
                          ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PENDING && (
                          <small
                            className={`d-block mt-1 ${
                              socialSecurityPassed ? "text-success" : "text-danger"
                            }`}
                          >
                            Updated {toTimestampText(
                              linkedCase.socialSecurityDecisionAt ||
                                linkedCase.socialSecurityApprovedAt,
                            )}
                          </small>
                        )}
                      </Col>
                    </Row>
                  </div>

                  <div className="border-top pt-3 mb-3">
                    <Row>
                      <Col md={5} className="mb-2">
                        <div className="font-weight-bold annual-mask-step-heading">4. Select mask <AnnualMaskStepStatus caseItem={linkedCase} step="mask" /></div>
                        <small className="text-muted">
                          Inventory balance and available lots are shown before selection.
                        </small>
                      </Col>
                      <Col md={7} className="mb-2">
                        <Form.Control
                          as="select"
                          size="sm"
                          value={safeText(
                            linkedCase.selectedMask &&
                              linkedCase.selectedMask.accessoryId,
                          )}
                          disabled={
                            saving ||
                            masksLoading ||
                            !socialSecurityPassed ||
                            saleOrderIsLocked
                          }
                          onChange={(event) => selectAnnualMask(event.target.value)}
                        >
                          <option value="">
                            {masksLoading ? "Loading mask stock…" : "Select mask type"}
                          </option>
                          {masks.map((mask) => (
                            <option
                              key={mask.id}
                              value={mask.id}
                              disabled={Number(mask.stockQuantity) <= 0}
                            >
                              {mask.itemName} {mask.size ? `(${mask.size})` : ""}
                              {` — Stock: ${Number(mask.stockQuantity) || 0}`}
                            </option>
                          ))}
                        </Form.Control>
                        {selectedMaskInventory && (
                          <small className="d-block text-muted mt-1">
                            Stock left: {selectedMaskInventory.stockQuantity}. Lots: {formatLots(selectedMaskInventory.lots)}
                          </small>
                        )}
                        {!masksLoading && masks.length === 0 && (
                          <small className="d-block text-warning mt-1">
                            No mask inventory items are available.
                          </small>
                        )}
                      </Col>
                    </Row>
                  </div>

                  <div className="border-top pt-3 mb-3">
                    <Row>
                      <Col md={5} className="mb-2">
                        <div className="font-weight-bold annual-mask-step-heading">
                          5. Select Sales Representative <AnnualMaskStepStatus caseItem={linkedCase} step="sale" />
                        </div>
                        <small className="text-muted">
                          The automatically created Sale Order will be assigned to the selected representative or Center.
                        </small>
                      </Col>
                      <Col md={7} className="mb-2">
                        <Form.Control
                          as="select"
                          size="sm"
                          value={selectedSaleId}
                          disabled={
                            !socialSecurityPassed || saving ||
                            saleOrderIsLocked
                          }
                          onChange={(event) => selectAnnualSale(event.target.value)}
                        >
                          <option value="">Select Sales Representative</option>
                          {selectedSaleId && !selectedSaleIsAvailable && (
                            <option value={selectedSaleId} disabled>
                              {selectedSaleName || selectedSaleId} — Not available
                            </option>
                          )}
                          {sales.map((sale) => (
                            <option key={sale.id} value={sale.id}>
                              {sale.name}
                            </option>
                          ))}
                        </Form.Control>
                        {!sales.length && (
                          <small className="d-block text-warning mt-1">
                            No available Sales Representatives were found.
                          </small>
                        )}
                        <small className="d-block text-muted mt-1">
                          Annual Mask Access notifications still go to every configured access account.
                        </small>
                      </Col>
                    </Row>
                  </div>

                  <div className="border-top pt-3 mb-3">
                    <Row>
                      <Col md={5} className="mb-2">
                        <div className="font-weight-bold annual-mask-step-heading">6. Expected mask receipt date <AnnualMaskStepStatus caseItem={linkedCase} step="receipt" /></div>
                        <small className="text-muted">
                          Save the date the patient is expected to receive the mask before Ready to Purchase.
                        </small>
                      </Col>
                      <Col md={7} className="mb-2">
                        <AnnualMaskReceiptDate embedded key={linkedCase.refPath} date={linkedCase.maskReceiptDate} disabled={saving || saleOrderIsLocked} onSave={saveMaskReceiptDate} />
                      </Col>
                    </Row>
                  </div>
                  <div className="border-top pt-3">
                    <Row className="align-items-center">
                      <Col md={5} className="mb-2">
                        <div className="font-weight-bold annual-mask-step-heading">7. Ready to purchase <AnnualMaskStepStatus caseItem={linkedCase} step="ready" /></div>
                        <small className="text-muted">
                          Confirms readiness, reserves one mask from its lots, and creates the Sale Order automatically.
                        </small>
                      </Col>
                      <Col md={7} className="mb-2">
                        <Button
                          size="sm"
                          variant="success"
                          onClick={markReadyToPurchase}
                          disabled={
                            saving ||
                            !socialSecurityPassed ||
                            !linkedCase.selectedMask ||
                            !linkedCase.selectedMask.accessoryId ||
                            !selectedSaleName ||
                            !selectedSaleIsAvailable ||
                            !isAnnualMaskReceiptDateValid(linkedCase.maskReceiptDate) ||
                            saleOrderIsLocked
                          }
                        >
                          <i className="fa fa-check mr-1" />
                          {linkedCase.saleOrderReceiptNo
                            ? `Order ${linkedCase.saleOrderReceiptNo} created`
                            : linkedCase.status ===
                              ANNUAL_MASK_STATUS.READY_TO_PURCHASE
                            ? "Retry & create Sale Order"
                            : "Ready & create Sale Order"}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  <div className="border-top pt-3 mt-3">
                    <Row className="align-items-center">
                      <Col md={5} className="mb-2">
                        <div className="font-weight-bold annual-mask-step-heading">8. Mark as เสร็จสิ้น <AnnualMaskStepStatus caseItem={linkedCase} step="completed" /></div>
                        <small className="text-muted">
                          Completes this purchase and sets the linked Sale Order to เสร็จสิ้น.
                        </small>
                      </Col>
                      <Col md={7} className="mb-2">
                        <AnnualMaskCompleteButton className="mb-1" caseItem={linkedCase} currentUser={currentUser} disabled={saving} onComplete={handlePurchaseComplete} />
                        {!linkedCase.saleOrderId && (
                          <small className="text-muted">Create the Sale Order in step 7 first.</small>
                        )}
                        {linkedCase.status === ANNUAL_MASK_STATUS.ORDER_COMPLETED && linkedCase.saleOrderStatus === "เสร็จสิ้น" && (
                          <span className="text-success">Purchase and Sale Order completed.</span>
                        )}
                      </Col>
                    </Row>
                  </div>
                </div>
              )}

              {!draftIsEditable && canManage && (
                <div className="alert alert-light border py-2 small mb-3">
                  Source details are locked because the doctor workflow has started. Continue the workflow using the actions above.
                </div>
              )}
              {readOnly && draftIsEditable && (
                <div className="alert alert-light border py-2 small mb-3">
                  Open this CPAP follow-up event in Edit mode to change the draft batch.
                </div>
              )}
              <Link
                to="/annual-mask-purchase"
                className="btn btn-outline-primary btn-sm"
              >
                Review all Annual Mask Purchase batches
              </Link>
            </>
          )}
        </Card.Body>
        </Card>
        {renderFilePreviewModal()}
      </>
    );
  }

  return (
    <Card className="border-primary">
      <Card.Header>
        <strong>
          {eventCases.length
            ? "Create Another Annual Mask Purchase Batch"
            : "Annual Mask Purchase"}
        </strong>
        <div className="small text-muted">
          Create a separate annual mask workflow for this patient from this CPAP follow-up event.
        </div>
      </Card.Header>
      <Card.Body>
        {renderEventBatchPicker()}
        {creatingAnotherCase && (
          <div className="alert alert-info py-2 small">
            The existing batches remain unchanged. This form creates a new case and keeps it in this event’s batch history.
          </div>
        )}
        {!canManage && (
          <div className="alert alert-light border mb-3 small">
            Only an administrator can create an Annual Mask Purchase.
          </div>
        )}
        {!hn && (
          <div className="alert alert-warning small">
            Add the patient HN before creating the Annual Mask Purchase.
          </div>
        )}
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Purchase batch</Form.Label>
              <Form.Control
                as="select"
                value={createForm.batchId}
                disabled={!canCreate || saving}
                onChange={(event) => updateCreateField("batchId", event.target.value)}
              >
                <option value="">Create new batch</option>
                {sortedBatches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.batchName || batch.id}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>New batch name</Form.Label>
              <Form.Control
                value={createForm.batchName}
                disabled={Boolean(createForm.batchId) || !canCreate || saving}
                onChange={(event) => updateCreateField("batchName", event.target.value)}
              />
              <Form.Text className="text-muted">
                Default: selected CPAP Usage Report upload month and patient name. Unsaved uploads use the current month. You can edit it before creating the batch.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Doctor</Form.Label>
              <Form.Control
                as="select"
                value={createForm.doctorId}
                disabled={!canCreate || saving}
                onChange={(event) => selectDoctor(event.target.value)}
              >
                <option value="">Select doctor</option>
                {doctors.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.name || doctor.doctorName || doctor.id}
                    {doctor.email ? ` — ${doctor.email}` : " (no email)"}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Sales assignment</Form.Label>
              <small className="form-text text-muted">
                After Social Security passes, select a Sales Representative in the workflow. This is required before Ready to Purchase; the Sale Order is then created automatically.
              </small>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={5}>
            {renderPdfPicker({
              label: "Therapy CPAP Usage Report",
              reports: usageReports,
              selectedKey: selectedUsageReportKey,
              onSelect: setSelectedUsageReportKey,
              emptyText: "No CPAP Usage Report in Therapy Data",
              helperText:
                "This PDF is attached to the annual case. Pressure comes from the current Therapy Min/Max fields.",
            })}
          </Col>
          <Col md={5}>
            {renderPdfPicker({
              label: "Sleep Study Report",
              reports: sleepStudyReports,
              selectedKey: selectedSleepStudyReportKey,
              onSelect: setSelectedSleepStudyReportKey,
              emptyText: "No Sleep Study Report",
              helperText:
                "Reads AHI, Min SpO₂, and Sleep Study Date from this PDF, then attaches it to the doctor email.",
            })}
          </Col>
          <Col md={2} className="d-flex align-items-end mb-3">
            <Button
              variant="outline-primary"
              size="sm"
              block
              onClick={fetchLatestClinicalValues}
              disabled={
                !canCreate ||
                saving ||
                fetchingClinicalData ||
                (!selectedSleepStudyReport &&
                  !therapyMinPressure &&
                  !therapyMaxPressure)
              }
            >
              {fetchingClinicalData ? "Reading…" : "Fetch clinical values"}
            </Button>
          </Col>
        </Row>
        <div
          className="border rounded p-3 mb-3"
          style={{ background: "#f8fafc" }}
        >
          <div className="d-flex justify-content-between align-items-start flex-wrap mb-3">
            <div>
              <div style={{ fontWeight: 700 }}>Medical certificate values</div>
              <small className="text-muted">
                Fetch fills these values from the sources shown below. You can review and edit them before creating the case.
              </small>
            </div>
            <div className="small text-muted mt-1">
              Pressure range: <strong>{formatAnnualMaskPressureRange({
                minPressure: createForm.minPressure,
                maxPressure: createForm.maxPressure,
              }) || "-"}</strong>{" "}
              cmH₂O
            </div>
          </div>

          <div className="mb-3">
            <div className="font-weight-bold mb-1">1. Sleep Study Report</div>
            <div className="small text-muted">
              AHI, Min SpO₂, and Sleep Study Date are read from the selected Sleep Study Report.
            </div>
            <div className="small text-muted mt-1">
              Selected: {selectedSleepStudyReport
                ? getReportName(selectedSleepStudyReport)
                : "No Sleep Study Report"}
            </div>
          </div>

          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  AHI <Badge variant="light">Sleep Study PDF</Badge>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  value={createForm.ahi}
                  disabled={!canCreate || saving}
                  onChange={(event) => updateCreateField("ahi", event.target.value)}
                />
                <Form.Text className="text-muted">Average AHI · events/hour</Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  Min SpO₂ <Badge variant="light">Sleep Study PDF</Badge>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  value={createForm.minSpo2}
                  disabled={!canCreate || saving}
                  onChange={(event) => updateCreateField("minSpo2", event.target.value)}
                />
                <Form.Text className="text-muted">Minimum oxygen saturation · %</Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  Sleep Study Date <Badge variant="light">Sleep Study PDF</Badge>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={createForm.latestSleepStudyDate}
                  disabled={!canCreate || saving}
                  onChange={(event) =>
                    updateCreateField("latestSleepStudyDate", event.target.value)
                  }
                />
                <Form.Text className="text-muted">Study date printed in the report</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="border-top pt-3 mb-3">
            <div className="font-weight-bold mb-1">2. CPAP Usage — Min/Max Pressure</div>
            <div className="small text-muted">
              Min/Max Pressure uses the current Therapy fields, including values read from a new local PDF before Save.
            </div>
            <div className="small text-muted mt-1">
              Current Therapy: Min {therapyMinPressure || "-"} · Max {therapyMaxPressure || "-"} cmH₂O
            </div>
          </div>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-md-0">
                <Form.Label>
                  Min Pressure <Badge variant="light">Therapy CPAP</Badge>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  value={createForm.minPressure}
                  disabled={!canCreate || saving}
                  onChange={(event) =>
                    updateCreateField("minPressure", event.target.value)
                  }
                />
                <Form.Text className="text-muted">Current minimum pressure · cmH₂O</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label>
                  Max Pressure <Badge variant="light">Therapy CPAP</Badge>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  value={createForm.maxPressure}
                  disabled={!canCreate || saving}
                  onChange={(event) =>
                    updateCreateField("maxPressure", event.target.value)
                  }
                />
                <Form.Text className="text-muted">Current maximum pressure · cmH₂O</Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </div>
        <Form.Group className="mb-0">
          <Form.Label>Upload different CPAP Usage Report (PDF, optional)</Form.Label>
          <Form.Control
            type="file"
            accept="application/pdf,.pdf"
            disabled={!canCreate || saving || fetchingClinicalData}
            onChange={(event) =>
              updateCreateField("usageFile", event.target.files[0] || null)
            }
          />
          {createForm.usageFile && (
            <small className="text-muted d-block mt-1">
              {createForm.usageFile.name}
            </small>
          )}
          {!createForm.usageFile && selectedUsageReport && (
            <small className="text-muted d-block mt-1">
              {selectedUsageReport.pendingUpload
                ? "The Annual Mask Purchase will use this local PDF now. Its Therapy copy remains Waiting to save."
                : `The Annual Mask Purchase case will use: ${getReportName(
                    selectedUsageReport,
                  )}`}
            </small>
          )}
        </Form.Group>
        <div className="d-flex justify-content-between align-items-center flex-wrap mt-3">
          <small className="text-muted">
            Patient: {getPatientName(getFollowupPatient(followup)) || "-"} · HN: {hn || "-"}
          </small>
          <div>
            {creatingAnotherCase && (
              <Button
                type="button"
                variant="outline-secondary"
                className="mr-2"
                onClick={cancelAnotherCase}
                disabled={saving}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="primary"
              onClick={createAnnualMaskCase}
              disabled={!canCreate || saving || fetchingClinicalData}
            >
              {saving
                ? "Creating…"
                : eventCases.length
                  ? "Create Another Batch"
                  : "Create Annual Mask Purchase"}
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default AnnualMaskPurchaseEventSection;
